/*
 * THE INTERFACE, IN THE READER'S LANGUAGE.
 *
 * A small typed dictionary — deliberately not a framework. The app's chrome is a bounded set of
 * phrases, and a bounded set is a record, not a runtime. Each key names a phrase the UI shows; each
 * language supplies the phrases it has. English is complete and is the fallback: anything a language
 * omits reads in English rather than as a blank, so a half-translated language degrades to readable.
 *
 * A phrase may carry `{name}` placeholders; `fill` substitutes them. Adding a member to `Strings`
 * forces English to define it (the type demands it) and lets every other language fill it in over time.
 */
/*
 * WHICH LANGUAGES EXIST, AND WHICH READ RIGHT TO LEFT, now come from core — re-exported here so
 * every caller in this app keeps importing them from the dictionary it already uses.
 *
 * They moved the day the phone grew an interface of its own. Two renderers each deciding for
 * themselves which codes exist and which are RTL is a disagreement nobody notices until a Hebrew
 * reader meets the screen the other one was never told about.
 */
export { LANGS, LANG_ORDER, fill, isLangCode } from '@versesverse/core';
export type { LangCode, LangMeta } from '@versesverse/core';
import { fill } from '@versesverse/core';
import type { LangCode } from '@versesverse/core';

/** Every phrase key the chrome can ask for. English must define all of them; others may be partial. */
export interface Strings {
  /* Shell + map overlay */
  'shell.loading': string;
  'shell.error': string; // {detail}
  /* The two things a reader can do when the library will not open — see `nav/rescue.ts`. The pages
     need none of what just failed, so the second is always a real offer. */
  'shell.retry': string;
  'shell.pages': string;
  'shell.staleLink': string;
  'map.summary': string; // {names}, {texts}
  /* On the wheel: how many figures only this scripture names, written under its own spoke. Theirs
     is the English literal `n+' named here only'` — this entry is why the dictionary exists. */
  'map.wedgeOnly': string;
  /** Both counts, when the map knows both — see the note where it is chosen. */
  'map.wedgeCounts': string;
  /** The card under the pointer: how many scriptures, and how two names are joined. */
  'map.hoverCorpora': string;
  'map.hoverJoin': string; // {count}
  /*
   * THE ENGINE'S READOUT, bottom left of the map — theirs writes these as English literals in its
   * markup. A diagnostic overlay is a legitimate place for a reader to be shown English, but it
   * should be the dictionary deciding that by falling back rather than a shipped file deciding it
   * by having no other option.
   */
  'map.hud.fps': string;
  'map.hud.ms': string;
  'map.hud.cells': string;
  'map.hud.sky': string;
  'map.hud.names': string;
  'map.hud.disc': string;
  'map.hud.dot': string;
  'map.hud.clump': string;
  'map.hud.strands': string;
  'map.hud.strandsOff': string;
  'map.hud.chunks': string;
  'map.hud.kilobytes': string;
  /** Ours, not theirs: the tilt and the zoom against the floor the fades are keyed to. */
  'map.hud.tilt': string; // {percent}
  'map.hud.zoom': string; // {zoom}, {floor}
  'map.hud.labels': string; // {count}
  'map.hud.fan': string; // {strands}, {passages}
  /** The still/live split — the whole performance story of this renderer. {still}, {rate}, {live} */
  'map.hud.split': string;
  /** What each MOVING layer drew — the line that makes a dead layer visible. */
  'map.hud.layers': string; // {travellers}, {seats}, {readers}, {dust}, {disputed}
  /* The reader-presence cluster. Deeds are KEYS rather than sentences so a feed can be read in
     Arabic and Hebrew; the reference's are six English literals in a `DEEDS` array. */
  'feed.exploring': string; // {count}
  'feed.voice': string;
  'compare.open': string;
  'compare.disputed': string;
  'compare.differ': string;
  'compare.numbers': string;
  'compare.toldTwice': string;
  'sources.open': string;
  'lang.label': string;

  /*
   * THE RUNGS OF THE MAP — what each ring of the ladder is called, and what is said on crossing it.
   *
   * Keyed by the rung index the bundle ships on every figure (`Bundle.layout[3]`), so the two
   * cannot drift: a figure carries a number, and the number is looked up here. The names live in
   * the dictionary rather than in the bundle precisely BECAUSE they are read aloud to a person —
   * shipping them as data would hand a Hebrew or Arabic reader English, which is the one thing
   * this dictionary exists to prevent. What the bundle carries is the index; what a reader sees
   * is a phrase, and the phrase has three versions.
   *
   * `tier.n.name` is the orientation pill, at the top of the map, and is now the only place a rung
   * is named to a reader. There was a `tier.n.crossing` beside each of these — a banner that fired
   * once when a step took the reader from one rung into another, explaining the rung rather than
   * labelling it. It went on 2026-08-22 on Ariel's instruction, with the component that showed it.
   */
  'tier.0.name': string;
  'tier.1.name': string;
  'tier.2.name': string;
  'tier.3.name': string;
  'tier.4.name': string;
  /** When there is no figure in front of the reader to name a rung from. */
  'tier.unknown': string;

  /*
   * HOW THE READER HAS BEEN READING, and where they have been.
   *
   * The posture is INFERRED and never asked — the design's five self-declared options exist in its
   * copy and are deliberately not offered as a choice, because asking somebody to pick a label
   * before they have read anything is asking them to perform an identity rather than have one. So
   * every one of these is the map reporting what it noticed, in the second person, and each says
   * plainly what it will do differently as a result. A posture inferred and not acted on would be
   * a horoscope.
   */
  /*
   * THE FLIGHT IN — what is said over the opening, one short line per beat.
   *
   * NOTHING HERE MAY BE INVENTED. A game's opening cinematic can describe a kingdom because the
   * kingdom is whatever it says; every line here has to be true of the build the reader is looking
   * at. So each is either counted from what shipped — the design's copy quotes its own prototype's
   * "993 names. 41,530 passages.", which is the exact staleness `SourcesPanel` has tests against —
   * or a fact about how this map is built. That is the tighter constraint and the better script.
   */
  /** {names} {passages} {texts} {traditions} */
  'tour.size': string;
  'tour.size.sub': string;
  'tour.centre': string;
  'tour.centre.sub': string;
  'tour.rim': string;
  'tour.rim.sub': string;
  /** {place} {reach} */
  'tour.place': string;
  'tour.place.sub': string;
  'tour.landing': string;
  'tour.landing.sub': string;
  /** The way out, on screen from the first second. */
  'tour.skip': string;


  /** What the corner control is labelled — two or three letters, beside who else is here. */
  'you.short': string;
  'you.open': string;
  'you.readingAs': string;
  'you.closestIn': string;
  'you.furthestOut': string;
  'you.nowhere': string;
  /** How much of each scripture has been opened — their YOUR ATLAS. */
  'you.atlas': string;
  'you.atlasRow': string;
  /** The travel list behind the orientation pill. */
  /**
   * SOMEBODY ELSE READING — the six remarks that drift over the map while `?mock=1` is on.
   *
   * Every one is about READING rather than about a figure, so none of them can be wrong about
   * what is under the pointer. See `mockAmbient`.
   */
  'travel.title': string;
  'travel.lead': string;
  'travel.return': string;
  'travel.look': string;
  'travel.walked': string;
  'travel.nowhere': string;
  'standpoint.comparatist': string;
  'standpoint.comparatist.why': string; // {texts}
  'standpoint.comparatist.effect': string;
  'standpoint.sceptic': string;
  'standpoint.sceptic.why': string;
  'standpoint.sceptic.effect': string;
  'standpoint.originals': string;
  'standpoint.originals.why': string;
  'standpoint.originals.effect': string;
  'standpoint.newcomer': string;
  'standpoint.newcomer.why': string;
  'standpoint.newcomer.effect': string;
  /** The line that makes everything above it safe to have said. */
  'standpoint.neverAsked': string;

  /* Shared across drawers/panels */
  'action.close': string;
  'action.back': string;
  'auth.google': string;
  'auth.signedInAs': string; // {name}
  'auth.signOut': string;
  /* A name of their own — §5.1's promise, which had no mechanism until 2026-08-18. */
  'auth.rename': string;
  'auth.renameLabel': string;
  'auth.renameSave': string;
  'auth.renameCancel': string;
  'auth.renameFailed': string;
  /* THE RIGHT TO LEAVE. The server has done this work since identity was built and no screen could
     ask for it — see `eraseMe` in core. Two presses rather than a typed word, because the second
     button says what it does in whatever language the reader is in. */
  'auth.erase': string;
  'auth.eraseWhat': string;
  'auth.eraseYes': string;
  'auth.eraseCancel': string;
  'auth.eraseFailed': string;
  /* Arriving back from a sign-in that did not finish — see `AppUrlState.signin`. */
  'auth.signInDidNotFinish': string;

  /*
   * WHAT THE SERVER REFUSED, IN THE READER'S LANGUAGE. The server writes its refusals in English
   * and cannot do otherwise — it does not know who is reading. So it names them instead
   * (`REFUSAL_REASONS` in core) and the words are here. One key per name, and the type demands the
   * pair: add a reason to core without a phrase here and `refusalWords` stops compiling.
   */
  'rooms.micDidNotOpen': string;
  /* The other direction, and the dangerous one: still audible while believing otherwise. */
  'rooms.micStillOn': string;
  'auth.sessionEnded': string;
  /* Open, and carrying no sound at all — a device, not a quiet reader. */
  'rooms.micSilent': string;
  /* The browser refusing to play what arrives — the label IS the sentence, see `ActiveRoom`. */
  'rooms.notPlaying': string;
  /* Following where the room is looking — §11. Two phrases, because the control says which. */
  'rooms.following': string;
  'rooms.followAgain': string;
  'rooms.micBlocked': string;
  'rooms.yoursHeading': string;
  'rooms.yoursClose': string;
  'rooms.yoursCloseNamed': string; // {title}
  'chat.notSent': string;
  'halls.addressFromName': string;
  'refusal.suspended': string;
  'refusal.roomFull': string; // {capacity}
  'refusal.stageFull': string; // {limit}
  'refusal.tooFast': string;
  'refusal.addressTaken': string;
  'refusal.tooManyRooms': string; // {count}
  'refusal.tooManyHalls': string; // {count}
  'refusal.tooManyCoHosts': string; // {limit}
  'refusal.hallIsFull': string; // {limit}
  'refusal.tooManyChannels': string; // {count}
  'refusal.tooManySessions': string; // {count}
  'refusal.notAName': string;
  'refusal.notALink': string; // {url}
  'refusal.addressNotUsable': string;
  'refusal.notADiscordWebhook': string;
  'refusal.notAYouTubeChannel': string;
  'refusal.postIsGone': string;
  'refusal.voiceUnreachable': string;
  'refusal.silencedButNotMuted': string;
  'refusal.nobodyMuted': string;
  /* The two acts whose record changed while the live half did not — see `REFUSAL_REASONS`. */
  'refusal.stillInTheRoom': string;
  'refusal.stageNotLive': string;

  /* Arrival */

  /* The first-nudge hint */
  'hint.text': string;
  /** The corner line, which follows the range — see map/v8/hint.ts. */
  'hint.dark': string;
  'hint.zoomIn': string;
  'hint.tap': string;
  'hint.flying': string;
  'hint.dismiss': string;

  /* Figure kinds (node panel + legend) */
  'type.person': string;
  'type.place': string;
  'type.people': string;
  'type.divine': string;
  'type.unknown': string;
  'type.object': string;
  'type.observance': string;
  'type.institution': string;
  'type.scripture': string;
  'type.angelic': string;
  'type.deity': string;
  'type.time': string;
  'type.language': string;
  'type.practice': string;
  'type.ruling': string;
  'type.event': string;
  'type.realm': string;
  'type.attribute': string;
  'type.religion': string;
  'type.tribe': string;
  'type.celestial': string;
  'type.concept': string;
  'type.parable': string;
  'type.prayer': string;
  'type.creature': string;

  /* Node panel */
  'panel.about': string; // {name}
  'panel.openDiscussion': string;
  'panel.namedIn': string;
  /** The line above NAMED IN — how far the name reaches. `{name}` `{named}` `{total}` `{text}`. */
  'panel.reachAll': string;
  'panel.reachSome': string;
  'panel.reachOnly': string;
  /** The title on a NAMED IN row, which narrows the map to that one scripture. `{text}`. */
  'panel.onlyThis': string;
  /** Where a reader can go next, and which way each one lies. `panel.linkedBy` takes `{ref}`. */
  'panel.trails': string;
  'panel.inward': string;
  'panel.outward': string;
  'panel.linkedBy': string;
  'panel.sharesName': string;
  'panel.identity': string;
  'panel.argued': string;
  'panel.identityHeld': string;
  'panel.identityDisputed': string;
  'unit.passage': string;
  'unit.passages': string;
  'panel.contested': string;
  'panel.shared': string;
  /*
   * HADITH ON A FIGURE'S PANEL.
   *
   * "Also related about" rather than "Hadith" or "Sayings": these are reports ABOUT the
   * figure, they are not scripture, and ROADMAP is explicit that the interface must not put
   * them beside the Quran at the same weight. The heading carries that distinction, since
   * no other part of the page can.
   *
   * `reportsArabicOnly` is not an apology. Every other passage here shows an original and an
   * English together; these cannot, and a reader who meets Arabic with no English is owed the
   * reason on the spot — but the reason is not the one this string gave until 2026-08-19.
   *
   * ═══════════════════════════════════════════════════════════════════════════════════════
   * IT SAID THE TRANSLATION DOES NOT EXIST. THAT IS A CLAIM ABOUT THE WORLD AND IT IS FALSE.
   *
   * `fetch-shia-hadith.ts` corrected the same sentence in its own output on 2026-08-18, and
   * recorded why: **30,856 of 48,936 Twelver records carry a human English translation from
   * seventeen named translators**, and al-Kāfī has two complete ones. They are not carried
   * because a keyer's blanket CC0 cannot dedicate rights the translators hold — all seventeen
   * are modern and none is out of term — so this is a permission nobody has been granted, not
   * a translation nobody has made.
   *
   * That stage states the standard this string now meets: calling it an absence *"turns
   * 'somebody could say yes' into 'nobody has done the work', and those have different
   * remedies."* **The log was corrected and the sentence a READER sees was not** — the same
   * shape as a fixed number kept behind a filter, which this project found twice on 2026-08-19.
   *
   * The Sunni shelves are in the same position for a different reason: sunnah.com's English is
   * refused in writing and Muhsin Khan's is in term until 2092.
   */
  'panel.reports': string; // {name}
  'panel.reportsArabicOnly': string;
  /**
   * Who translated the English shown under a report — never omitted where one is shown.
   *
   * A translation is a reading, and unattributed it reads as this library's own. The same rule
   * `edition` and `gradedBy` are required under.
   */
  'panel.reportsEnglishFrom': string;
  /**
   * Said under a report whose text stops mid-word, and under no other.
   *
   * Athar cuts one matn in nine at 200 characters; 469 reports across five shelves arrive that
   * way, and the Zaydi and Ibadi branches are the worst hit. A reader would otherwise take a
   * fragment for a short hadith.
   */
  'panel.reportsCut': string;
  'panel.reportsCutTitle': string;
  'panel.reportsNumbering': string;
  'panel.reportsChain': string;
  'panel.reportsChainWho': string;
  'panel.reportsCites': string;
  'panel.reportsAlso': string;
  'panel.reportsAgreed': string;
  'panel.reportsAgreedTitle': string;
  'panel.reportsPlaceTitle': string;
  'panel.reportsPlaceInferredTitle': string;
  'panel.reportsQuotedFrom': string;
  'panel.reportsQuotedFromTitle': string;
  /*
   * THE COMPILER'S OWN VERDICT ON A REPORT — 2026-08-19.
   *
   * Twelve terms, because that is what the compilers say and not what a three-value field could
   * hold: al-Tirmidhi's commonest verdict is hasan sahih, which is neither sahih nor hasan, and
   * gharib and mursal are statements about a chain rather than degrees of soundness. The list is
   * `Grade` in schema/reports.ts and `Verdict` in pipeline/lib/compiler-verdict.ts — the same
   * twelve under two names, because the domain has two words for it.
   *
   * The transliterated term is kept in every language and the GLOSS is translated. A reader who
   * knows the word should meet the word; a reader who does not should not have to look it up.
   */
  'panel.reportsGraded': string; // {who}
  'panel.reportsGradedTitle': string;
  'panel.gradeHasanSahih': string;
  'panel.gradeHasanGharib': string;
  'panel.gradeSahih': string;
  'panel.gradeHasan': string;
  'panel.gradeGharib': string;
  'panel.gradeDaif': string;
  'panel.gradeMunkar': string;
  'panel.gradeMursal': string;
  'panel.gradeMudtarib': string;
  'panel.gradeMukhtasar': string;
  'panel.gradeMansukh': string;
  'panel.gradeMalul': string;
  'panel.branchSunni': string;
  'panel.branchTwelver': string;
  'panel.branchZaydi': string;
  'panel.branchIbadi': string;
  'panel.branchTitle': string;
  /* Where a chain stops — the Prophet, a Companion, a Successor. See schema/hadith-layer.ts. */
  'panel.layerMarfu': string;
  'panel.layerQudsi': string;
  'panel.layerMawquf': string;
  'panel.layerMaqtu': string;
  'panel.reportsSplit': string; // {named}, {narrated}
  'panel.reportsNarrated': string; // {name}
  'panel.sharedLead': string;
  'panel.differLead': string;
  'panel.filterPlaceholder': string;
  'panel.filterLabel': string; // {name}
  'panel.chipsLabel': string;
  'panel.noMatch': string;
  'panel.showing': string; // {shown}, {total}
  'panel.sortLabel': string;
  'panel.sortInOrder': string;
  'panel.sortLeadFirst': string; // {name}
  'panel.noPassages': string;
  'panel.noPassagesReportsOnly': string;

  /* Verses list */
  'verses.loading': string;
  /** The same, once the count is known — the ids arrive long before the text. */
  'verses.loadingCount': string; // {count}
  'verses.heading': string;
  /** Why the passages are in this order — see verses/sortPassages.ts. */
  'verses.orderEach': string;
  'verses.orderFrom': string;
  'verses.notLoaded': string;
  /* How many OTHER Englishes this verse carries — the caption doubles as the door to them. */
  'verses.otherTranslations': string;
  'verses.nonCommercialTitle': string;
  'verses.nonCommercial': string;
  'verses.hebrewNumbering': string;
  'verses.hebrewNumberingWhy': string;
  'verses.noOriginal': string; // {edition}
  'verses.readOn': string; // {source}
  'verses.discuss': string;
  'verses.incomplete': string;

  /* Machine verse translation */
  'translate.hide': string;
  'translate.to': string; // {lang}
  'translate.inProgress': string;
  'translate.caveat': string;
  'translate.unsupported': string; // {lang}
  'translate.error': string;

  /* Discussion */
  'discussion.edit': string;
  'discussion.editing': string;
  'discussion.save': string;
  'discussion.cancelEdit': string;
  'discussion.edited': string;
  'discussion.report': string;
  'discussion.reported': string;
  'discussion.remove': string;
  'discussion.postError': string;
  'discussion.label': string;
  'discussion.add': string;
  'discussion.placeholder': string;
  'discussion.post': string;
  'discussion.toTakePart': string;
  'discussion.loading': string;
  /* The older half of a busy node's discussion, which nothing could reach. */
  'discussion.earlier': string;
  /*
   * Which is not the same as the discussion starting here — see the panel's `earlier`.
   *
   * The PHONE already had this key, this failure and this sentence; the web panel was the one
   * missing the catch. So the wording is the phone's rather than a second one for the same
   * event: one key, one sentence, and a phrase somebody chose is not mine to reword in passing.
   */
  'discussion.earlierFailed': string;
  'discussion.empty': string;
  'discussionDrawer.label': string; // {title}
  'discussionDrawer.heading': string; // {title}

  /* Voice */
  'voice.label': string;
  'voice.heading': string;
  'voice.leave': string;
  'voice.toJoin': string;

  /* Rooms */
  'reports.heading': string;
  'reports.failed': string;
  'reports.keep': string;
  'reports.remove': string;
  'reports.times': string; // {count}
  'reports.departed': string;
  'reports.more': string; // {count}
  'reports.suspend': string; // {name}
  'reports.reinstate': string;
  'reports.history': string;
  'reports.earlier': string;
  'reports.wasSuspended': string;
  'reports.wasSomething': string;
  'reports.wasReinstated': string;
  'rooms.empty': string;
  'rooms.openOne': string;
  'rooms.whoMayCome': string;
  'rooms.accessPublic': string;
  'rooms.accessPassphrase': string;
  'rooms.accessInvite': string;
  'rooms.setPassphrase': string;
  'rooms.invitePlaceholder': string;
  'rooms.useInvite': string;
  'rooms.shareInvite': string;
  'rooms.underHall': string;
  'rooms.commons': string;
  /* When the reader's own communities could not be READ, which is not the same as having none. */
  'halls.couldNotReadYours': string;
  'rooms.titlePlaceholder': string;
  'rooms.opening': string;
  'rooms.roomLabel': string; // {title}
  'rooms.inRoom': string; // {count}
  'rooms.nobodyYet': string;
  'rooms.full': string; // {count}
  'rooms.barred': string;
  'rooms.notHost': string;
  'rooms.close': string;
  'rooms.barredHeading': string;
  'rooms.letBackIn': string; // {name}
  'rooms.departedReader': string;
  'rooms.lockDoor': string;
  'rooms.openDoor': string;
  'rooms.newCode': string;
  'rooms.silence': string;
  'rooms.hostBadge': string;
  'rooms.listening': string;
  /* Their audio never reached THIS browser — see `RoomParticipant.unheard`. */
  'rooms.unheard': string;
  'rooms.muteSelf': string;
  'rooms.unmuteSelf': string;
  'rooms.participants': string;
  /* How full the stage is. Shown only where there IS a stage — see `stageSeats` in core. */
  'rooms.stageSeats': string; // {taken} {limit}
  'rooms.showEveryone': string; // {count}
  'rooms.moreAsking': string; // {count}
  'rooms.forceMute': string; // {name}
  'rooms.hushForMe': string; // {name}
  'rooms.hearAgain': string; // {name}
  'rooms.toStage': string; // {name}
  'rooms.offStage': string; // {name}
  'rooms.kick': string; // {name}
  'rooms.ban': string;
  'rooms.makeHost': string; // {name}
  /** The line the room desk folds behind — making a room, and redeeming an invite to one. */
  'rooms.deskOpen': string;
  'rooms.talkHere': string;
  'rooms.locked': string;
  'rooms.passphrasePlaceholder': string;
  'rooms.enter': string;
  'rooms.back': string;
  'rooms.wrongPassphrase': string;
  'rooms.wentWrong': string;
  'rooms.reconnecting': string;
  'rooms.handUp': string;
  'rooms.handDown': string;
  'rooms.asking': string; // {count}
  'rooms.notNow': string; // {name}
  'rooms.speakNext': string; // {name}
  'rooms.coHosting': string;
  'rooms.coHostAsked': string;
  'rooms.coHostEnd': string;
  'rooms.coHostAsk': string;
  'rooms.coHostPlaceholder': string;
  'rooms.coHostNoSuchHall': string;
  'rooms.removedYou': string;
  'rooms.hostClosed': string;
  'rooms.joinedElsewhere': string;
  'rooms.connectionLost': string;

  /* In-room chat */
  'chat.label': string;
  'chat.heading': string;
  'chat.empty': string;
  'chat.placeholder': string;
  'chat.send': string;

  /* Scheduled sessions */
  'sessions.label': string;
  'sessions.heading': string;
  'sessions.empty': string;
  'sessions.weekly': string;
  'sessions.joinNow': string;
  /*
   * A TIME THAT COULD NOT BE READ — `sessions.` until three surfaces needed it.
   *
   * It was named for the one list that had it, and then the Hall's record and the moderator's
   * desk started saying it too — because the guard beside it moved out of the studies panel and
   * into `readableTime`. **A name that says which list a phrase belongs to is a name somebody
   * will edit for that list**, and the other two would change with it and nobody would look.
   * `shared.` is where `couldNotRead` already lives, for the same reason.
   */
  'shared.timeUnknown': string;
  'sessions.underHall': string;
  'sessions.cancel': string;
  'sessions.yours': string;
  'sessions.goThere': string; // {name}
  'sessions.cancelThis': string; // {name}
  'sessions.whenLabel': string;
  'sessions.titlePlaceholder': string;
  'sessions.everyWeek': string;
  /* How long a study runs — the promise the room is held for, not a cosmetic default. */
  /* Inside the hour, HOW SOON is the only useful fact — see whenOf. */
  'sessions.startsIn': string;
  'sessions.lasts': string; // {count} // {count}
  'sessions.startingNow': string;
  'sessions.howLong': string;
  'sessions.minutes': string; // {count}
  'sessions.schedule': string;
  'sessions.scheduling': string;
  'sessions.toSchedule': string;
  'sessions.badTime': string;
  'sessions.failed': string;
  'sessions.newPassphrase': string;
  'sessions.changeIt': string;
  'sessions.rekeyThis': string; // {name}
  'sessions.whoMayCome': string;
  'sessions.setPassphrase': string;
  'sessions.calledOff': string;
  'sessions.hallGone': string;
  'sessions.notPermitted': string;

  /* The Discord bridge — a leader's own channels (COMMUNITY.md §6) */
  'channels.heading': string;
  'channels.connect': string;
  'channels.connecting': string;
  'channels.urlLabel': string;
  'channels.urlHelp': string;
  'channels.thisPassage': string;
  'channels.everyPassage': string;
  'channels.followsThis': string;
  'channels.followsAll': string;
  'channels.retired': string;
  'channels.retiredDeleted': string;
  'channels.retiredRefused': string;
  'channels.followsSome': string; // {count}
  'channels.disconnect': string;
  'channels.reconnect': string;
  'channels.none': string;
  'channels.failed': string;

  /* Halls */
  'hall.label': string;
  'hall.loading': string;
  'hall.missing': string;
  /* Which is NOT the same as there being no community at that address — see `hall`. */
  'hall.unreadable': string;
  /* And when the COMMUNITY read fine but where this reader stands did not. */
  'hall.standingUnknown': string;
  'hall.back': string;
  'hall.verifiedMark': string;
  'hall.verifiedAs': string; // {handle}
  'hall.join': string;
  'hall.leave': string;
  'hall.settings': string;
  'hall.nameLabel': string;
  'hall.descriptionLabel': string;
  'hall.linksLabel': string;
  'hall.linkKind': string;
  'hall.linkUrl': string;
  'hall.linkLabel': string;
  'hall.addLink': string;
  'hall.dropLink': string;
  'hall.doorLabel': string;
  'hall.doorOpen': string;
  'hall.doorApproval': string;
  'hall.listingLabel': string;
  'hall.listingPublic': string;
  'hall.listingUnlisted': string;
  'hall.save': string;
  'hall.saving': string;
  'hall.team': string;
  'shared.couldNotRead': string;
  'hall.bar': string; // {name}
  'hall.barred': string;
  'hall.letBackIn': string; // {name}
  'hall.coHost': string;
  'hall.makeCoHost': string;
  'hall.standDown': string;
  'hall.departed': string;
  'hall.log': string;
  'hall.logEarlier': string;
  'hall.showMore': string;
  'hall.invited': string;
  'hall.acceptCoHost': string;
  'hall.declineCoHost': string;
  'log.mute': string; // {name}
  'log.onStage': string; // {name}
  'log.offStage': string; // {name}
  'log.remove': string; // {name}
  'log.ban': string; // {name}
  'log.unban': string; // {name}
  'log.addHost': string; // {name}
  'log.silence': string;
  'log.closeRoom': string;
  'log.doorLocked': string;
  'log.doorOpened': string;
  'log.unnamed': string; // {name}
  'log.moderatorAdded': string; // {name}
  'log.moderatorRemoved': string; // {name}
  'log.hallBanned': string; // {name}
  'log.hallUnbanned': string; // {name}
  'log.coHostAsked': string;
  'log.coHostAccepted': string;
  'log.coHostEnded': string;
  'hall.waiting': string;
  /* The applicant's own half of that queue: the leader could see them knocking, and they could not. */
  'hall.waitingToBeLetIn': string;
  'hall.withdraw': string;
  'hall.approve': string;
  /* When somebody knocked — the queue is oldest-first, and the order carries no duration. */
  'hall.asked': string; // {when}
  'hall.talkingNow': string;
  'hall.comingUp': string;
  'hall.roomHere': string; // {count}
  'hall.roomQuiet': string;
  'hall.cannotJoin': string;
  'hall.ownerOnly': string;
  'hall.ownerTools': string;
  'hall.delete': string;
  'hall.deleteConfirm': string;
  'hall.deleteFinal': string;
  'hall.deleteCancel': string;
  'hall.export': string;
  'hall.verify': string;
  'hall.handlePlaceholder': string;
  'hall.challengeLead': string; // {handle}
  'hall.checkNow': string;
  'hall.toTakePart': string;
  'hall.failed': string;
  'halls.label': string;
  'halls.heading': string;
  'halls.yours': string;
  'halls.public': string;
  'halls.none': string;
  'halls.noneYours': string;
  'halls.startOne': string;
  /* The one decision a leader could not make when they made the Hall. Phrased as what it DOES, not
     as 'unlisted' — the checkbox is on by default, so the words have to describe the on state. */
  'halls.listInDirectory': string;
  /* The size of a community, said plainly. A count, never a roster — see HallPage. */
  'hall.memberCount': string; // {count}
  /* The same size WITH the ceiling, for whoever the ceiling can change something for — see
     `hallPlaces` in core. Not on the halls list, which is a chooser between communities. */
  'hall.memberOfLimit': string; // {count} {limit}
  'halls.opening': string;
  'halls.namePlaceholder': string;
  'halls.addressPlaceholder': string;
  'halls.toStart': string;
  'halls.failed': string;
  'halls.browse': string;

  /* Expanded reader */
  'reader.label': string; // {unit}
  'reader.labelFallback': string;
  'reader.heading': string;
  'reader.alsoTold': string; // {ref}
  'reader.alsoCounted': string; // {ref}

  /* Side-by-side captions (built in App) */
  'story.sharedTitle': string; // {figure}
  'story.sharedCaption': string; // {shared}
  'story.toldTitle': string; // {group}
  'story.toldCaption': string; // {count}

  /* Told-twice panel */
  'told.lead': string;
  'told.shared': string; // {count}

  /* Differences / numbers panels */
  'differ.lead': string;
  'numbers.lead': string;
  'differences.readBoth': string;

  /* Disputed panel */
  'disputed.badgeSameName': string;
  'disputed.badgeDisputed': string;
  'disputed.heading': string;
  'disputed.lead': string;
  'disputed.weighIn': string;
  'disputed.seeOnMap': string; // {name}

  /* Contested passages — the disputed panel's second half, measured rather than curated */
  'contested.passagesHeading': string;
  'contested.passagesLead': string;
  'contested.tradition.christian': string;
  'contested.tradition.muslim': string;
  'contested.tradition.jewish': string;
  'contested.open': string; // opens the disputed panel from the map overlay
  'contested.alongside': string;

  /* Sources panel */
  'sources.lead': string;
  'sources.labelEnglish': string;
  'sources.labelSecond': string;
  'sources.labelThird': string;
  /* A fourth English and beyond — as many as can be licensed, each named with its own terms. */
  'sources.labelAlso': string;
  'sources.renderingGate': string;
  'sources.heldAsScripture': string;
  'sources.notHeldAsScripture': string;
  'sources.alsoHeldBy': string;
  'sources.notInCanonOf': string;
  'sources.labelOriginal': string;
  /* Where a text has no published English at all — the hadith, and only the hadith. */
  'sources.noEnglish': string;
  'sources.heldBack': string;
  'sources.heldBackAffects': string;
  /* Branches the library carries nothing of. Distinct from heldBack, which is ours and withheld. */
  'sources.absent': string;
  'sources.absentLead': string;
  'sources.absentSplit': string;
  'sources.absentHolds': string;
  'sources.unshelved': string;
  'sources.unshelvedLead': string; // {count}
  'sources.unshelvedPassages': string;
  'sources.blockedPermission': string;
  'sources.blockedOriginal': string;
  'sources.blockedAddressing': string;
  'sources.blockedTranslation': string;
  'sources.blockedTranscription': string;
  'sources.expiries': string;
  'sources.expiriesLead': string;
  'sources.freeFrom': string; // {date}
  'sources.expiryMonths': string; // {n}
  'sources.expiryYears': string; // {n}
  'sources.heldBackLead': string;
  'sources.awaiting': string; // {name}

  /* Search */
  'search.label': string;
  'search.placeholder': string;
  'search.nothing': string; // {term}
  /** A citation is not a name, so the sentence above stays; this offers what it IS. */
  'search.openPassage': string; // {ref}

  /* Offline */
  'offline.saving': string;
  'offline.savingCount': string; // {done}, {total}
  'offline.save': string;
  'offline.saved': string; // {saved}, {total}
  'offline.error': string;

  /* Legend */
  'legend.open': string;
  'legend.scriptures': string;
  'legend.shapes': string;
  'legend.people': string;
  /* A shape is a GROUP of kinds — see MapLegend. Named by what the group is, not by one member. */
  'legend.shapeBeings': string;
  'legend.shapeThings': string;
  'legend.shapeBodies': string;
  /* The grey a node wears when no scripture names it — see MapLegend. */
  'legend.noScripture': string;
  'legend.marks': string;
  /* The circle size, which is the only thing the map says without words — see MapLegend. */
  'legend.size': string;
  'legend.gold': string;
  'legend.faint': string;
}

const en: Strings = {
  'shell.loading': 'Opening the library…',
  'shell.error': 'The library could not be reached. {detail}',
  'shell.retry': 'Try again',
  'shell.pages': 'Read it as a page instead',
  'shell.staleLink': 'That link asked for something this library does not hold.',
  'map.summary': '{names} names · {texts} texts',
  'map.wedgeOnly': '{count} named here only',
  'map.wedgeCounts': '{held} names · {alone} only here',
  'map.hoverCorpora': '{count} corpora',
  'map.hoverJoin': ' & ',
  'map.hud.fps': 'fps',
  'map.hud.ms': 'ms',
  'map.hud.cells': 'cells',
  'map.hud.sky': 'sky',
  'map.hud.names': 'names',
  'map.hud.disc': 'disc',
  'map.hud.dot': 'dot',
  'map.hud.clump': 'clump',
  'map.hud.strands': 'strands',
  'map.hud.strandsOff': 'strands off at this range',
  'map.hud.chunks': 'chunks',
  'map.hud.kilobytes': 'KB',
  'map.hud.tilt': 'tilt {percent}%',
  'map.hud.zoom': 'z {zoom} of {floor}',
  'map.hud.labels': '{count} labels',
  'map.hud.fan': 'fan {strands}/{passages} cited',
  'map.hud.split': 'still {still}ms ×{rate}/s · live {live}ms',
  'map.hud.layers': 'trav {travellers} · seats {seats} · readers {readers} · dust {dust} · disputed {disputed}',
  'feed.exploring': '{count} exploring',
  'feed.voice': 'Live voice',
  'compare.open': 'Compare the accounts',
  'compare.disputed': 'Where a figure is disputed',
  'compare.differ': 'Where the accounts differ',
  'compare.numbers': 'Numbers that differ',
  'compare.toldTwice': 'Told twice inside one book',
  'sources.open': 'Sources & licences',
  'lang.label': 'Language',

  /* The rungs. The crossing lines are the design's own copy, minus its first rung: it gave the
     single Name a ring of its own and this map does not — every name of God shares one. */
  'tier.0.name': 'THE NAMES OF GOD',
  'tier.1.name': 'THE UNSEEN',
  'tier.2.name': 'SPOKEN TO',
  'tier.3.name': 'NAMED',
  'tier.4.name': 'THE NATIONS',
  'tier.unknown': 'THE NAMED WORLD',

  'tour.size': '{names} names. {passages} passages.',
  'tour.size.sub': '{texts} books, held by {traditions} traditions. Every one of these names is somebody’s.',
  'tour.centre': 'At the centre, the names of God.',
  'tour.centre.sub': 'Every tradition puts something different there, and this map does not choose.',
  'tour.rim': 'Out here the naming stops.',
  'tour.rim.sub': 'Not the world — only what these books put a name to. The light beyond is everything they do not.',
  'tour.place': '{place}.',
  'tour.place.sub': '{reach} names stand within one step of here.',
  'tour.landing': 'You start here.',
  'tour.landing.sub': 'The rest stays dark until you go there.',
  'tour.skip': 'Skip',

  /* Theirs is 'YOU'. The stylesheet does the capitals, so a language that has no capitals is
     not asked for any. */
  'you.short': 'You',
  'you.open': 'You — how you have been reading',
  'you.readingAs': 'YOU HAVE BEEN READING AS',
  'you.closestIn': 'CLOSEST IN',
  'you.furthestOut': 'FURTHEST OUT',
  'you.nowhere': 'You have not opened a name yet.',
  'you.atlas': 'YOUR ATLAS',
  'you.atlasRow': '{got} of {tot}',
  'travel.title': 'Where to?',
  'travel.lead': 'Return anywhere you have walked. Places you have not are shown from a distance.',
  'travel.return': 'return',
  'travel.look': 'set out',
  'travel.walked': '{walked} of {total} walked',
  'travel.nowhere': 'The map has no neighbourhoods to return to yet.',
  /* The design's own copy for the four postures. */
  'standpoint.comparatist': 'the comparatist',
  'standpoint.comparatist.why': 'You crossed {texts} texts without being asked to.',
  'standpoint.comparatist.effect': 'Shared names and doubled stories surface first.',
  'standpoint.sceptic': 'the sceptic',
  'standpoint.sceptic.why': 'You stopped where the traditions disagree.',
  'standpoint.sceptic.effect': 'Disputed identities and divergent accounts stay unsmoothed.',
  'standpoint.originals': 'the reader of originals',
  'standpoint.originals.why': 'You kept opening the text under the translation.',
  'standpoint.originals.effect': 'The original sits above the English, not below it.',
  'standpoint.newcomer': 'the newcomer',
  'standpoint.newcomer.why': 'You walked without a map and did not ask for one.',
  'standpoint.newcomer.effect': 'Nothing is introduced as though you already knew it.',
  'standpoint.neverAsked': 'You are never asked which tradition you hold. Change this whenever.',

  'action.close': 'Close',
  'action.back': 'Back',
  'auth.google': 'Sign in with Google',
  'auth.signedInAs': 'Signed in as {name}',
  'auth.signOut': 'Sign out',
  'auth.rename': 'Change name',
  'auth.renameLabel': 'What you are called here',
  'auth.renameSave': 'Save',
  'auth.renameCancel': 'Cancel',
  'auth.renameFailed': 'That name could not be saved.',
  'auth.erase': 'Erase my account',
  'auth.eraseWhat': 'This takes your posts, the communities you run and the studies you have promised. It cannot be undone.',
  'auth.eraseYes': 'Erase everything',
  'auth.eraseCancel': 'Keep my account',
  'auth.eraseFailed': 'That did not work — nothing has been erased.',
  'auth.signInDidNotFinish': 'Signing in did not finish, so nothing has changed. Press to dismiss.',
  'rooms.micDidNotOpen': 'Your microphone did not open — press again.',
  'rooms.micStillOn': 'Your microphone is still on — we could not turn it off.',
  'auth.sessionEnded': 'Your session has ended, so that did not happen. Reload the page to sign in again.',
  'rooms.micSilent': 'Your microphone is open and sending no sound — check the device, then press to try again.',
  'rooms.notPlaying': 'Your browser is not playing the room — press here to hear it',
  'rooms.following': 'Following the room',
  'rooms.followAgain': 'Follow the room again',
  'rooms.micBlocked': 'Your browser is blocking the microphone for this site, and a room will not open without one. Allow it and try again.',
  'rooms.yoursHeading': 'Rooms you have open',
  'rooms.yoursClose': 'Close',
  'rooms.yoursCloseNamed': 'Close {title}',
  'chat.notSent': 'That did not send — your words are back in the box, try again.',
  'halls.addressFromName': 'An address could not be made from that name — type one below, in Latin letters and digits.',
  'refusal.suspended': 'This account has been suspended.',
  'refusal.roomFull': 'This room is full ({capacity}) — try again in a moment, or start another.',
  'refusal.stageFull': 'The stage is full ({limit}).',
  'refusal.tooFast': 'That was a lot of requests at once — wait a moment and try again.',
  'refusal.addressTaken': 'That address is taken.',
  'refusal.tooManyRooms': 'Close one of your {count} open rooms before opening another.',
  'refusal.tooManyHalls': 'You already run {count} halls.',
  'refusal.tooManyCoHosts': 'A hall may have {limit} co-hosts — stand one down first.',
  'refusal.tooManySessions': 'Call off one of your {count} scheduled studies before adding another.',
  'refusal.tooManyChannels': 'Disconnect one of your {count} channels before connecting another.',
  'refusal.hallIsFull': 'This hall holds {limit} members — somebody has to leave before another can be let in.',
  'refusal.notAName': 'That is not a name.',
  'refusal.notALink': '{url} is not a link.',
  'refusal.addressNotUsable': 'That address is not usable — lowercase letters, digits and hyphens, and not a reserved word.',
  'refusal.notADiscordWebhook': 'That is not a Discord channel webhook address.',
  'refusal.notAYouTubeChannel': 'That is not a YouTube channel address.',
  'refusal.postIsGone': 'That post is no longer there — it may have been removed.',
  'refusal.voiceUnreachable': 'The voice service is not reachable right now — the room is still here, so try again in a moment.',
  'refusal.silencedButNotMuted': 'The room is set so that only hosts may speak, and whoever arrives now arrives listening — but the voice service could not be reached to mute the people already talking, so try again in a moment.',
  'refusal.nobodyMuted': 'The voice service could not be reached, so nobody has been muted — try again in a moment.',
  'refusal.stillInTheRoom': 'The room server could not be reached, so they are still in the room — try again in a moment.',
  'refusal.stageNotLive': 'The stage has changed, but the room server could not be reached — it will take effect when they next join.',


  'hint.text': 'Tap any name to open it — then follow the links in each verse to travel between the scriptures.',
  'hint.dark': 'the dark is what you have not surveyed',
  'hint.zoomIn': 'zoom in to read the names',
  'hint.tap': 'tap a name to open its verses',
  'hint.flying': 'esc to leave · drag to keep flying',
  'hint.dismiss': 'Dismiss',

  'type.person': 'Person',
  'type.place': 'Place',
  'type.people': 'People',
  'type.divine': 'The divine',
  'type.unknown': 'Unclassified',
  'type.object': 'Object',
  'type.observance': 'Observance',
  'type.institution': 'Institution',
  'type.scripture': 'Scripture',
  'type.angelic': 'Angel or spirit',
  'type.deity': 'Deity',
  'type.time': 'Time',
  'type.language': 'Language',
  'type.practice': 'Practice',
  'type.ruling': 'Ruling',
  'type.event': 'Event',
  'type.realm': 'Realm',
  'type.attribute': 'Name or title',
  'type.religion': 'Religion',
  'type.tribe': 'Tribe',
  'type.celestial': 'Constellation',
  'type.concept': 'Concept',
  'type.parable': 'Parable',
  'type.prayer': 'Prayer',
  'type.creature': 'Creature',

  'panel.about': 'About {name}',
  'panel.openDiscussion': 'Open discussion',
  'panel.namedIn': 'Named in',
  /* No count in the "all" line. Theirs can write "all four" because four is a word; ours would have
     to write "all 5", and spelling the numeral is not portable — Arabic agrees number with noun in
     gender and has a dual. The list directly below says how many, so nothing is lost. */
  'panel.reachAll': '{name} is named in all of them.',
  'panel.reachSome': '{name} is named in {named} of the {total}.',
  'panel.reachOnly': '{name} is named only in the {text}.',
  'panel.onlyThis': 'Show only the {text}',
  'panel.trails': 'Trails from here',
  'panel.inward': 'inward',
  'panel.outward': 'outward',
  'panel.linkedBy': 'Linked by {ref}',
  'panel.sharesName': 'Another figure answers to this name:',
  'panel.identity': 'Names some hold to be one:',
  'panel.argued': 'Argued over, in these passages:',
  'panel.identityHeld': 'Held by',
  'panel.identityDisputed': 'Disputed by',
  'unit.passage': 'passage',
  'unit.passages': 'passages',
  'panel.contested': 'Whether this is one figure across the scriptures is disputed.',
  'panel.shared': 'Shared passages',
  'panel.reports': 'Also related about {name}',
  /*
   * CORRECTED 2026-08-20, because it stopped being true that morning. It read "Arabic only.
   * English translations of these collections exist; they are under copyright and this library has
   * not been granted them" — and 1,960 reports now carry HadeethEnc's English, which IS granted.
   * A note explaining an absence has to go when the absence does.
   */
  /*
   * CORRECTED TWICE ON 2026-08-20, and the second correction is the one that matters.
   *
   * The first version explained a total absence and went false when 2,565 reports gained an
   * English. The replacement said "some reports carry an English translation from HadeethEnc" —
   * true, and it hid WHICH reports. HadeethEnc selects under the methodology of Ahl al-Sunna
   * wa-l-Jamāʿa, so its English lands on Sunni shelves and nowhere else:
   *
   *   sunni     220,807 reports · 2,565 with an English
   *   twelver    72,795 · 0
   *   ibadi         827 · 0
   *   zaydi         257 · 0
   *
   * A Twelver reader meeting 72,795 untranslated reports while Sunni ones carry an English, with
   * nothing on the page saying why, is being told something this library does not mean. The
   * asymmetry is a fact about who granted permission, and it has to be said rather than left to
   * look like a judgement — the same reason `partialCanon` exists on the corpus.
   */
  'panel.reportsArabicOnly': 'Mostly Arabic only. HadeethEnc permits its English and it is shown wherever it matches a report here — but it is a Sunni encyclopedia’s selection, so it reaches Sunni collections and none of the Twelver, Zaydi or Ibadi ones. Elsewhere English translations exist and are under copyright, and this library has not been granted them.',
  'panel.reportsEnglishFrom': 'English translation by HadeethEnc',
  'panel.reportsCut': 'This report is cut short — the source it comes from stops here, mid-word.',
  'panel.reportsCutTitle': 'The supplier’s file limits each report to 200 characters. The rest of the wording was never delivered and is not missing from the collection itself.',
  'panel.reportsNumbering': 'Where a reference here is a name and a number alone, it is the digitiser’s own sequence rather than a printed edition’s, and will not match a citation from elsewhere.',
  'panel.reportsChain': 'Chain of transmission',
  'panel.reportsChainWho': 'The line under a name is who the corpus says that man is, and when he died — a chain gives the short name that identified him to the people who heard it. The name above is what this book actually prints.',
  'panel.reportsCites': 'Quotes',
  'panel.reportsAlso': 'Also names',
  'panel.reportsAgreed': 'Also in',
  'panel.reportsAgreedTitle': 'Both collections carry this report — muttafaq alayh, agreed upon',
  'panel.reportsPlaceTitle': 'Where this report sits in the collection — the compiler’s own book and chapter, located in a named printed edition',
  'panel.reportsPlaceInferredTitle': 'The book is inferred from the reports either side of this one, which sit in it; the chapter is not known',
  'panel.reportsQuotedFrom': 'Quoted from',
  'panel.reportsQuotedFromTitle': 'The earlier work this report was taken from. Mustadrak al-Wasaʾil is a supplement: al-Nuri collected what the Wasaʾil left out, so its reports are older than the book that carries them.',
  'panel.reportsGraded': '{who} judged this',
  'panel.reportsGradedTitle': 'The compiler’s own verdict on his own report, in his own words — not this library’s, and not a modern editor’s. A grading is one scholar’s opinion of a report, and it is shown with his name because detached from him it means nothing.',
  'panel.gradeHasanSahih': 'hasan sahih — good and sound',
  'panel.gradeHasanGharib': 'hasan gharib — good, and reported by only one chain',
  'panel.gradeSahih': 'sahih — sound',
  'panel.gradeHasan': 'hasan — good',
  'panel.gradeGharib': 'gharib — reported by only one chain',
  'panel.gradeDaif': 'daif — weak',
  'panel.gradeMunkar': 'munkar — rejected',
  'panel.gradeMursal': 'mursal — the chain stops short of the Prophet',
  'panel.gradeMudtarib': 'mudtarib — the chains conflict',
  'panel.gradeMukhtasar': 'mukhtasar — abridged from a longer report',
  'panel.gradeMansukh': 'mansukh — abrogated by a later ruling',
  'panel.gradeMalul': 'maʻlul — carrying a hidden defect',
  'panel.branchSunni': 'Sunni',
  'panel.branchTwelver': 'Twelver Shia',
  'panel.branchZaydi': 'Zaydi Shia',
  'panel.branchIbadi': 'Ibadi',
  'panel.branchTitle': 'Which branch of Islam holds the collection this report stands in. This library carries shelves from four, and a figure’s page lists them together.',
  'panel.layerMarfu': 'from the Prophet',
  'panel.layerQudsi': 'God\u2019s words, from the Prophet',
  'panel.layerMawquf': 'stops at a Companion',
  'panel.layerMaqtu': 'stops at a Successor',
  'panel.reportsSplit': '{named} name this figure · {narrated} were passed on by them',
  'panel.reportsNarrated': 'passed on by {name}',
  'panel.sharedLead': 'The same episode, told in the Bible and the Quran — read side by side.',
  'panel.differLead': 'Where this figure’s account differs between the Bible and the Quran — read both, as a question, never a verdict.',
  'panel.filterPlaceholder': 'Filter — text, book, or 1:1',
  'panel.filterLabel': "Filter {name}'s passages",
  'panel.chipsLabel': 'Show or hide a scripture',
  'panel.noMatch': 'No passages match.',
  'panel.showing': 'Showing {shown} of {total}',
  'panel.sortLabel': 'Sort',
  'panel.sortInOrder': 'In order',
  'panel.sortLeadFirst': '{name} first',
  'panel.noPassages': 'No verses are linked here — the scriptures point to this figure by description, or by a name too widely used to tag verse by verse, so there is no list of passages to open.',
  'panel.noPassagesReportsOnly': 'Neither the Bible nor the Quran names this figure. They are here because the hadith name them — what is related about them is above.',

  'verses.loading': 'Opening the passages…',
  'verses.loadingCount': 'Opening {count} passages…',
  'verses.heading': 'Passages',
  'verses.orderEach': 'Ordered one from each, in turn.',
  'verses.orderFrom': 'Ordered from the {text} first, then one from each in turn.',
  'verses.notLoaded': 'This passage did not load.',
  'verses.otherTranslations': '{n} more',
  'verses.nonCommercialTitle': "This translation's licence forbids selling it.",
  'verses.nonCommercial': 'non-commercial',
  /* The reference on the page is King James numbering. Where a Tanakh numbers the verse
     differently, it is named here — short, because it sits inline beside the citation. */
  'verses.hebrewNumbering': 'Hebrew',
  'verses.hebrewNumberingWhy': 'References here follow the King James. Hebrew Bibles number this chapter differently.',
  'verses.noOriginal': '{edition} carries no text at this reference.',
  'verses.readOn': 'Read on {source}',
  'verses.discuss': 'Discuss this verse',
  'verses.incomplete': 'Some passages could not be loaded — the links above open them elsewhere.',

  'translate.hide': 'Hide auto-translation',
  'translate.to': 'Auto-translate to {lang}',
  'translate.inProgress': 'Translating…',
  'translate.caveat': 'Automatic — a rough machine reading, not an authoritative translation.',
  'translate.unsupported': 'No on-device {lang} translation in this browser.',
  'translate.error': 'Couldn’t translate this verse.',

  'discussion.edit': 'Edit',
  'discussion.editing': 'Correct your post',
  'discussion.save': 'Save',
  'discussion.cancelEdit': 'Cancel',
  'discussion.edited': '(edited)',
  'discussion.report': 'Report',
  'discussion.reported': 'Reported',
  'discussion.remove': 'Remove',
  'discussion.postError': 'Could not post — please try again.',
  'discussion.label': 'discussion',
  'discussion.add': 'Add to the discussion',
  'discussion.placeholder': 'Add to the discussion…',
  'discussion.post': 'Post',
  'discussion.toTakePart': ' to take part.',
  'discussion.loading': 'Loading the discussion…',
  'discussion.earlier': 'Earlier posts',
  'discussion.earlierFailed': 'Could not load earlier posts.',
  'discussion.empty': 'No one has written here yet.',
  'discussionDrawer.label': 'Discussion — {title}',
  'discussionDrawer.heading': '{title} — discussion',

  'voice.label': 'voice room',
  'voice.heading': 'Voice',
  'voice.leave': 'Leave voice',
  'voice.toJoin': ' to join.',

  /* A moderator's queue. Absent entirely for everybody else, and absent when it is empty. */
  'reports.heading': 'Reported posts',
  'reports.failed': 'That did not go through — nothing has changed. Try again.',
  /* The verb a moderator means: they are LEAVING it, having looked. 'Dismiss' describes what
     happens to the report, and what matters is what happens to the post. */
  'reports.keep': 'Leave it',
  'reports.remove': 'Take it down',
  'reports.times': 'reported {count}×',
  'reports.departed': 'a departed reader',
  'reports.more': 'and {count} more waiting',
  /* The person, not the post — the emergency control, and it says whose account it ends. */
  'reports.suspend': 'Suspend {name}',
  'reports.reinstate': 'Let them back in',
  'reports.history': 'What moderators did',
  'reports.earlier': 'Earlier',
  'reports.wasSuspended': 'suspended by',
  'reports.wasSomething': 'acted on by',
  'reports.wasReinstated': 'let back in by',
  'rooms.empty': 'No one is talking here yet.',
  'rooms.openOne': 'Open a room',
  'rooms.whoMayCome': 'Who may come in',
  'rooms.accessPublic': 'Anyone',
  'rooms.accessPassphrase': 'With a passphrase',
  'rooms.accessInvite': 'By invitation only',
  'rooms.setPassphrase': 'Set a passphrase',
  'rooms.invitePlaceholder': 'Paste an invite code',
  'rooms.useInvite': 'Use it',
  'rooms.shareInvite': 'Send people this code: ',
  'rooms.underHall': 'Open it under',
  'rooms.commons': 'Anyone',
  'halls.couldNotReadYours': 'We could not read your communities just now, so this will not be opened under one.',
  'rooms.titlePlaceholder': 'What is this room about?',
  'rooms.opening': 'Opening…',
  'rooms.roomLabel': 'Join “{title}”',
  'rooms.inRoom': '{count} here',
  'rooms.nobodyYet': 'empty',
  /* With the NUMBER, because "full" alone invites a second tap: it says the room has a size and
     that it has been reached, not that something failed. */
  'rooms.full': 'full ({count})',
  'rooms.barred': 'This room is not open to you.',
  'rooms.notHost': 'Only a host can do that.',
  'rooms.close': 'Close room',
  /* A verb about the DOOR, not about the room: 'Make private' would read as a setting, and this is
     an act taken in the middle of one bad minute. */
  'rooms.barredHeading': 'Barred from this room',
  /* The verb a host actually means. 'Unban' is the database's word for it. */
  'rooms.letBackIn': 'Let {name} back in',
  'rooms.departedReader': 'a departed reader',
  'rooms.lockDoor': 'Lock the door',
  'rooms.openDoor': 'Open to anyone',
  'rooms.newCode': 'New code',
  'rooms.silence': 'Silence the room',
  'rooms.hostBadge': 'host',
  'rooms.listening': 'listening',
  'rooms.unheard': 'not reaching you',
  'rooms.muteSelf': 'Mute me',
  'rooms.unmuteSelf': 'Unmute me',
  'rooms.participants': 'In this room',
  'rooms.stageSeats': '{taken} of {limit} on stage',
  /* A count, not 'show more': the number IS the information — a reader is deciding whether 6 more
     people are worth a tap or 274 are. */
  'rooms.showEveryone': 'Show the other {count}',
  /* A number and no list: the queue's order is its fairness, so what a host needs is whether to keep
     taking questions — not a way to pick one person out of three hundred. */
  'rooms.moreAsking': '{count} more waiting',
  'rooms.forceMute': 'Mute {name}',
  /* NOT 'Mute' — a host's mute stops {name} for the room, and this stops them for you alone. Two
     controls that did the same thing to different scopes under one word would be a trap. */
  'rooms.hushForMe': 'Silence {name} for me',
  'rooms.hearAgain': 'Hear {name} again',
  'rooms.toStage': 'Let {name} speak',
  'rooms.offStage': 'Stop {name} speaking',
  'rooms.kick': 'Remove {name}',
  'rooms.ban': 'Ban {name}',
  'rooms.makeHost': 'Make {name} a host',
  'rooms.deskOpen': 'Open a room, or use an invite',
  'rooms.talkHere': 'Talk about this',
  'rooms.locked': 'needs a passphrase',
  'rooms.passphrasePlaceholder': 'Passphrase',
  'rooms.enter': 'Enter',
  'rooms.back': 'Back',
  'rooms.wrongPassphrase': 'That passphrase does not open this room.',
  'rooms.wentWrong': 'That did not work just now. Nothing is lost — try again in a moment.',
  'rooms.reconnecting': 'The connection dropped — trying to get back into the room…',
  /* 'Ask to speak', not 'Raise hand' — the gesture is a metaphor and the request is the thing. */
  'rooms.handUp': 'Ask to speak',
  'rooms.handDown': 'Never mind',
  'rooms.asking': '{count} asking to speak',
  'rooms.notNow': 'Not now, {name}',
  /* Distinct from the roster's 'Let {name} speak', which is on screen at the same time: two
     controls sharing an accessible name are ambiguous to anyone and unusable with a screen
     reader. 'next' also says what the queue is FOR. */
  'rooms.speakNext': 'Let {name} speak next',
  /* 'Asked', not 'co-hosting': until they accept, nothing about this room has changed. */
  'rooms.coHostAsked': 'Asked another community to co-host this. ',
  'rooms.coHosting': 'Co-hosted with another community. ',
  'rooms.coHostEnd': 'End that',
  'rooms.coHostAsk': 'Ask them to co-host',
  'rooms.coHostPlaceholder': 'the address of their community',
  'rooms.coHostNoSuchHall': 'No community has that address.',
  /* Says what happened and not who decided it: a host removing somebody is a decision about this
     room, and naming the person would turn a quiet moderation act into a public one. */
  'rooms.removedYou': 'A host removed you from that room.',
  'rooms.hostClosed': 'The host closed that room.',
  'rooms.joinedElsewhere': 'You joined that room somewhere else, so this one let go.',
  'rooms.connectionLost': 'The connection to that room was lost.',

  'chat.label': 'room chat',
  'chat.heading': 'Chat',
  'chat.empty': 'Nothing said since you arrived.',
  'chat.placeholder': 'Say something…',
  'chat.send': 'Send',

  'sessions.label': 'scheduled sessions',
  'sessions.heading': 'Coming up',
  'sessions.empty': 'Nothing scheduled here yet.',
  'sessions.weekly': 'every week',
  'sessions.joinNow': 'Join now',
  'shared.timeUnknown': 'time unknown',
  'sessions.underHall': 'Hold it under',
  'sessions.cancel': 'Cancel',
  'sessions.yours': 'Yours, elsewhere',
  'sessions.goThere': 'Go to {name}',
  'sessions.cancelThis': 'Cancel {name}',
  'sessions.whenLabel': 'When',
  'sessions.titlePlaceholder': 'What will you study?',
  'sessions.everyWeek': 'Every week',
  'sessions.startsIn': 'in {count} minutes',
  'sessions.lasts': 'for {count} minutes',
  'sessions.startingNow': 'starting now',
  'sessions.howLong': 'How long it runs',
  'sessions.minutes': '{count} minutes',
  'sessions.schedule': 'Schedule it',
  'sessions.scheduling': 'Scheduling…',
  'sessions.toSchedule': ' to schedule one.',
  'sessions.badTime': 'That is not a time.',
  'sessions.failed': 'That did not work — try again.',
  'sessions.newPassphrase': 'New passphrase',
  'sessions.changeIt': 'Change it',
  'sessions.rekeyThis': 'Change the passphrase for {name}',
  'sessions.whoMayCome': 'Who may come to the study',
  'sessions.setPassphrase': 'A passphrase for the study',
  'sessions.calledOff': 'Called off',
  'sessions.hallGone': 'the community it was held under was deleted',
  'sessions.notPermitted': 'you can no longer hold studies under that community',

  'channels.heading': 'Announce to Discord',
  'channels.connect': 'Connect',
  'channels.connecting': 'Connecting…',
  'channels.urlLabel': 'Discord webhook URL',
  'channels.urlHelp': 'In Discord: Channel settings → Integrations → Webhooks → Copy URL.',
  'channels.thisPassage': 'When a room opens on this passage',
  'channels.everyPassage': 'When a room opens anywhere',
  'channels.followsThis': 'Announcing rooms on this passage',
  'channels.followsAll': 'Announcing rooms everywhere',
  'channels.retired': 'Stopped — reconnect it',
  'channels.retiredDeleted': 'Stopped — that webhook no longer exists in Discord. Make a new one and connect it.',
  'channels.retiredRefused': 'Stopped — Discord refused it. Check the channel’s permissions, then reconnect.',
  'channels.followsSome': 'Announcing {count} passages',
  'channels.disconnect': 'Disconnect',
  'channels.reconnect': 'Connect it again',
  'channels.none': 'No channel connected.',
  'channels.failed': 'That did not work — try again.',

  'hall.label': 'hall',
  'hall.loading': 'Opening the hall…',
  'hall.missing': 'There is no hall at that address.',
  'hall.unreadable': 'We could not read that community just now — it is still there, try again in a moment.',
  'hall.standingUnknown': 'We could not tell whether you are in this community just now — try again in a moment.',
  'hall.back': 'Back to the map',
  'hall.verifiedMark': '✓ verified',
  'hall.verifiedAs': 'Proved control of {handle}',
  'hall.join': 'Join this hall',
  'hall.leave': 'Leave',
  'hall.settings': 'Your hall’s front page',
  'hall.nameLabel': 'Name',
  'hall.descriptionLabel': 'What it is for',
  'hall.linksLabel': 'Links out',
  'hall.linkKind': 'What kind of link',
  'hall.linkUrl': 'Address',
  'hall.linkLabel': 'What to call it',
  'hall.addLink': 'Add a link',
  'hall.dropLink': 'Remove',
  'hall.doorLabel': 'Who may join',
  'hall.doorOpen': 'Anyone may join',
  'hall.doorApproval': 'You approve each person',
  'hall.listingLabel': 'In the directory',
  'hall.listingPublic': 'Listed',
  'hall.listingUnlisted': 'By link only',
  'hall.save': 'Save',
  'hall.saving': 'Saving…',
  'hall.team': 'Who helps you run it',
  'shared.couldNotRead': 'This could not be read just now — it is not empty, try again in a moment.',
  /* The community's own door, not the site's: barring somebody here says nothing about them
     anywhere else, which is the promise D22 makes to leaders and to whoever they turn away. */
  'hall.bar': 'Bar {name}',
  'hall.barred': 'Barred from this community',
  'hall.letBackIn': 'Let {name} back in',
  'hall.coHost': 'co-host',
  'hall.makeCoHost': 'Make co-host',
  'hall.standDown': 'Stand down',
  'hall.departed': 'a departed reader',
  'hall.log': 'What your moderators did',
  /* A record has to be walkable, or the disputes it exists to settle are the ones it cannot reach. */
  'hall.logEarlier': 'Earlier',
  'hall.showMore': 'Show more',
  /* A question waiting for an answer, so it is phrased as one. */
  'hall.invited': 'You have been asked to co-host',
  'hall.acceptCoHost': 'Co-host it',
  'hall.declineCoHost': 'No thank you',
  /* Past tense and plain: this is a record of something that happened, not a description of a
     control. A leader reads it to answer 'who did that, and when'. */
  'log.mute': 'muted {name}',
  'log.onStage': 'let {name} speak',
  'log.offStage': 'took {name} off the stage',
  'log.remove': 'removed {name}',
  'log.ban': 'banned {name}',
  'log.unban': 'lifted the ban on {name}',
  'log.addHost': 'made {name} a host',
  'log.silence': 'silenced the room',
  'log.closeRoom': 'closed the room',
  /* Which WAY it went, because a leader reading this needs to know whether their study is now
     behind a lock or open to the map — not merely that somebody touched the door. */
  'log.doorLocked': 'locked the door',
  'log.doorOpened': 'opened the door to anyone',
  /* The community, not the room: this reaches every room the Hall holds and outlives all of them. */
  'log.unnamed': 'acted on {name} — this page is older than the record',
  'log.moderatorAdded': 'made {name} a co-host',
  'log.moderatorRemoved': 'stood {name} down as co-host',
  'log.hallBanned': 'barred {name} from the community',
  'log.hallUnbanned': 'let {name} back into the community',
  /* The three whose effect is public — they move where this community's name appears. */
  'log.coHostAsked': 'asked another community to co-host a room',
  'log.coHostAccepted': 'agreed to co-host a room for another community',
  'log.coHostEnded': 'ended a co-hosting arrangement',
  'hall.waiting': 'Waiting to join',
  'hall.waitingToBeLetIn': 'You have asked to join. A leader will let you in.',
  'hall.withdraw': 'Withdraw',
  'hall.approve': 'Let them in',
  'hall.asked': 'asked {when}',
  'hall.talkingNow': 'Talking now',
  'hall.comingUp': 'Coming up',
  'hall.roomHere': '{count} here',
  'hall.roomQuiet': 'just opened',
  'hall.cannotJoin': 'You cannot join this hall.',
  'hall.ownerOnly': 'Only the owner can do that.',
  'hall.ownerTools': 'Yours to run',
  'hall.delete': 'Delete this hall',
  'hall.deleteConfirm': 'Type {slug} to confirm',
  'hall.deleteFinal': 'Delete it, for good',
  'hall.deleteCancel': 'Keep it',
  'hall.export': 'Download everything',
  'hall.verify': 'Prove it is yours',
  'hall.handlePlaceholder': 'Your YouTube channel',
  'hall.challengeLead': 'Put this anywhere in the description for {handle}, then check.',
  'hall.checkNow': 'Check now',
  'hall.toTakePart': ' to take part.',
  'hall.failed': 'That did not work — try again.',
  'halls.label': 'halls',
  'halls.heading': 'Halls',
  'halls.yours': 'Yours',
  'halls.public': 'Open to anyone',
  'halls.none': 'No halls yet.',
  'halls.noneYours': 'You are not in a hall yet.',
  'halls.startOne': 'Start a hall',
  'halls.listInDirectory': 'List it where people can find it',
  'hall.memberCount': '{count} members',
  'hall.memberOfLimit': '{count} of {limit} members',
  'halls.opening': 'Opening…',
  'halls.namePlaceholder': 'What is your community called?',
  'halls.addressPlaceholder': 'address',
  'halls.toStart': ' to start one.',
  'halls.failed': 'That did not work — try again.',
  'halls.browse': 'Halls',

  'reader.label': 'Reading {unit}',
  'reader.labelFallback': 'Reading the passage',
  'reader.heading': 'Passage',
  'reader.alsoTold': 'Also told in {ref}',
  'reader.alsoCounted': 'Counted differently in {ref}',

  'story.sharedTitle': '{figure} — one episode, two scriptures',
  'story.sharedCaption': 'Paired because both use: {shared}. A suggestion to read both, not a claim they say the same thing.',
  'story.toldTitle': '{group} — told twice',
  'story.toldCaption': '{count} verses in common — two tellings inside one scripture, shown side by side, not judged.',

  'told.lead': 'The same chapter, told twice within one scripture — shown side by side, not judged.',
  'told.shared': '{count} shared',

  'differ.lead': 'Places the Bible and the Quran tell one episode differently — shown as a question with both readings, not a verdict.',
  'numbers.lead': 'Places one scripture gives two different numbers for the same thing — with the caution that the same word can count different things.',
  'differences.readBoth': 'Read both accounts',

  'disputed.badgeSameName': 'Same name, likely different',
  'disputed.badgeDisputed': 'Disputed identity',
  'disputed.heading': 'Disputed identities',
  'disputed.lead': 'Figures the traditions name — or don’t — the same way. Is this one person across the scriptures, or two who share a name? Read the case, then weigh in. Shown as an open question, never a verdict.',
  'disputed.weighIn': 'Weigh in',
  'disputed.seeOnMap': 'See {name} on the map',
  'contested.passagesHeading': 'Passages people argue over',
  'contested.passagesLead': 'Measured, not chosen: these are the passages more than one tradition was heard arguing over. Open one and read it.',
  'contested.tradition.christian': 'Christian',
  'contested.tradition.muslim': 'Muslim',
  'contested.tradition.jewish': 'Jewish',
  'contested.open': 'Where people disagree',
  'contested.alongside': 'argued alongside',

  'sources.lead': 'Every translation named, and what is held back and why.',
  'sources.labelEnglish': 'English',
  'sources.labelSecond': 'Second English',
  'sources.labelThird': 'Third English',
  'sources.labelAlso': 'Also in',
  /* Why a rendering present on one verse is absent on another. The alternative to withholding
     is showing a reader the wrong verse under a right-looking citation, which no reader can
     detect — so the omission is deliberate and has to be sayable. */
  'sources.renderingGate': 'Each of these is shown only where it can be matched verse for verse, and is left out rather than shown against the wrong verse. Usually that is chapter division: a translation that divides verses differently cannot be lined up. Where a rendering is short for some other reason — a canon that never held those books, a transcription with gaps — its own note says which.',
  'sources.heldAsScripture': 'Held as scripture by',
  'sources.notHeldAsScripture': 'Not by',
  /* Deliberately "also": the tradition named here holds everything above and more, and a
     wording that implied it held something else instead would be false. */
  'sources.alsoHeldBy': 'Also held as scripture by',
  'sources.notInCanonOf': 'Not in the canon of',
  'sources.labelOriginal': 'Original',
  'sources.noEnglish': 'No published English translation — read in Arabic, or as a marked machine reading',
  'sources.heldBack': 'Held back',
  'sources.heldBackAffects': 'Affects',
  'sources.absent': 'Branches not represented here',
  /* Says whose fault it is, because the alternative reads as a judgement on the branch. */
  'sources.absentLead': 'None of these traditions is represented in this library yet. In every case that is a licensing or digitisation problem on our side, never a statement about the texts.',
  'sources.absentSplit': 'Of the {total}, {awaited} are absent entirely — nobody has said yes. For the other {partly}, part of the text is already here and still cannot be shown as a reader of that branch would need it.',
  'sources.absentHolds': 'Would come looking for',
  'sources.unshelved': 'Held, and not yet shelved',
  'sources.unshelvedLead': 'These texts are here and free to publish. {count} passages are waiting on a decision about where they belong — the hold-up is ours, not a publisher’s.',
  'sources.unshelvedPassages': 'passages held',
  'sources.blockedPermission': 'awaiting permission',
  'sources.blockedOriginal': 'no publishable original',
  /* The obstacle is ours, not the world's — the text is free and held, our shape does not fit it. */
  'sources.blockedAddressing': 'not divided into verses',
  'sources.blockedTranslation': 'no published translation',
  /* Also ours rather than the world's, and the only blocker anybody can remove without asking:
     the text is free and in hand, and its scan is not yet accurate enough to publish. */
  'sources.blockedTranscription': 'free, but not yet accurately transcribed',
  /* The only hopeful absence on this page: these arrive whether anyone acts or not. */
  'sources.expiries': 'Arriving on a date',
  'sources.expiriesLead': 'Not missing for good — these are under copyright until a term runs out, and the date is known.',
  'sources.freeFrom': 'free in {date}',
  'sources.expiryMonths': 'about {n} months away',
  'sources.expiryYears': 'about {n} years away',
  'sources.heldBackLead': 'Not shipped — a licence question, not an editorial one.',
  'sources.awaiting': 'awaiting {name}',

  'search.label': 'Search the map for a name',
  'search.placeholder': 'Search a name — Abraham, Musa…',
  'search.nothing': 'No name here matches “{term}”.',
  'search.openPassage': 'Open {ref} →',

  'offline.saving': 'Saving for offline…',
  'offline.savingCount': 'Saving for offline… {done} of {total}',
  'offline.save': 'Save for offline',
  'offline.saved': 'Saved {saved} of {total} files for offline reading.',
  'offline.error': 'Could not save for offline.',

  'legend.open': 'Legend — what am I looking at?',
  'legend.scriptures': 'Scriptures',
  'legend.shapes': 'Shapes',
  'legend.people': 'People or nation',
  'legend.shapeBeings': 'A person, or one who is worshipped',
  'legend.shapeThings': 'A place, a thing, or a scripture',
  'legend.shapeBodies': 'A people, a season, or a shared institution',
  'legend.noScripture': 'Grey — no scripture names this one; described but unnamed, or named only in the hadith',
  'legend.marks': 'Marks',
  'legend.size': 'Bigger — named in more verses; the faint ones are sized on a smaller scale of their own',
  'legend.gold': 'Gold ring — whether this is one figure across the scriptures is disputed; open it to read the case and weigh in',
  'legend.faint': 'Faint — found by reading the texts, not checked by hand',
};

const he: Partial<Strings> = {
  'shell.loading': 'פותח את הספרייה…',
  'shell.error': 'לא ניתן היה להגיע לספרייה. {detail}',
  'shell.retry': 'לנסות שוב',
  'shell.pages': 'לקרוא כדף רגיל במקום',
  'shell.staleLink': 'הקישור ביקש משהו שאין בספרייה הזאת.',
  'map.summary': '{names} שמות · {texts} טקסטים',
  'map.wedgeOnly': '{count} נזכרים רק כאן',
  'map.wedgeCounts': '{held} שמות · {alone} רק כאן',
  'map.hoverCorpora': '{count} כתבים',
  'map.hoverJoin': ' ו',
  'map.hud.fps': 'fps',
  'map.hud.ms': 'ms',
  'map.hud.cells': 'תאים',
  'map.hud.sky': 'שמיים',
  'map.hud.names': 'שמות',
  'map.hud.disc': 'עיגול',
  'map.hud.dot': 'נקודה',
  'map.hud.clump': 'גוש',
  'map.hud.strands': 'חוטים',
  'map.hud.strandsOff': 'החוטים כבויים בטווח הזה',
  'map.hud.chunks': 'מקטעים',
  'map.hud.kilobytes': 'KB',
  'map.hud.tilt': 'הטיה {percent}%',
  'map.hud.zoom': 'z {zoom} מתוך {floor}',
  'map.hud.labels': '{count} תוויות',
  'map.hud.fan': 'מניפה {strands}/{passages} מצוטטים',
  'map.hud.split': 'קבוע {still}ms ×{rate}/ש · חי {live}ms',
  'map.hud.layers': 'נוסעים {travellers} · מושבים {seats} · קוראים {readers} · אבק {dust} · שנוי {disputed}',
  'feed.exploring': '{count} מטיילים',
  'feed.voice': 'קול חי',
  'compare.open': 'השוואת המקורות',
  'compare.disputed': 'דמויות שנויות במחלוקת',
  'compare.differ': 'היכן המקורות נבדלים',
  'compare.numbers': 'מספרים שאינם תואמים',
  'compare.toldTwice': 'מסופר פעמיים באותו ספר',
  'sources.open': 'מקורות ורישיונות',
  'lang.label': 'שפה',

  'tier.0.name': 'שמות האל',
  'tier.1.name': 'הנסתר',
  'tier.2.name': 'מי שדובר אליו',
  'tier.3.name': 'נזכרים בשם',
  'tier.4.name': 'האומות',
  'tier.unknown': 'העולם הנקוב בשם',

  'tour.size': '{names} שמות. {passages} קטעים.',
  'tour.size.sub': '{texts} ספרים, שמחזיקות בהם {traditions} מסורות. כל אחד מהשמות האלה שייך למישהו.',
  'tour.centre': 'במרכז — שמותיו של אלוהים.',
  'tour.centre.sub': 'כל מסורת מציבה שם משהו אחר, והמפה הזו אינה בוחרת.',
  'tour.rim': 'כאן נגמרת הקריאה בשם.',
  'tour.rim.sub': 'לא העולם — רק מה שהספרים האלה נתנו לו שם. האור שמעבר הוא כל מה שלא.',
  'tour.place': '{place}.',
  'tour.place.sub': '{reach} שמות עומדים במרחק צעד אחד מכאן.',
  'tour.landing': 'כאן אתה מתחיל.',
  'tour.landing.sub': 'השאר נשאר חשוך עד שתלך לשם.',
  'tour.skip': 'דלג',

  'you.short': 'אתה',
  'you.open': 'אתה — איך קראת עד כה',
  'you.readingAs': 'קראת עד כה בתור',
  'you.closestIn': 'הקרוב ביותר פנימה',
  'you.furthestOut': 'הרחוק ביותר החוצה',
  'you.nowhere': 'עדיין לא פתחת שום שם.',
  'you.atlas': 'האטלס שלך',
  'you.atlasRow': '{got} מתוך {tot}',
  'travel.title': 'לאן?',
  'travel.lead': 'חזרו לכל מקום שבו הלכתם. מקומות שלא — נראים מרחוק.',
  'travel.return': 'חזרה',
  'travel.look': 'צאו לדרך',
  'travel.walked': '{walked} מתוך {total} נסקרו',
  'travel.nowhere': 'למפה אין עדיין אזורים לחזור אליהם.',
  'standpoint.comparatist': 'המשווה',
  'standpoint.comparatist.why': 'חצית {texts} טקסטים בלי שביקשו ממך.',
  'standpoint.comparatist.effect': 'שמות משותפים וסיפורים כפולים יעלו ראשונים.',
  'standpoint.sceptic': 'הספקן',
  'standpoint.sceptic.why': 'עצרת במקום שבו המסורות חלוקות.',
  'standpoint.sceptic.effect': 'זהויות שנויות במחלוקת ותיאורים סותרים יישארו בלי החלקה.',
  'standpoint.originals': 'קורא המקור',
  'standpoint.originals.why': 'שוב ושוב פתחת את הטקסט שמתחת לתרגום.',
  'standpoint.originals.effect': 'המקור יישב מעל האנגלית, לא מתחתיה.',
  'standpoint.newcomer': 'הבא לראשונה',
  'standpoint.newcomer.why': 'הלכת בלי מפה ולא ביקשת אחת.',
  'standpoint.newcomer.effect': 'שום דבר לא יוצג כאילו כבר ידעת אותו.',
  'standpoint.neverAsked': 'לעולם לא נשאל אותך באיזו מסורת אתה מחזיק. אפשר לשנות את זה מתי שתרצה.',

  'action.close': 'סגירה',
  'action.back': 'חזרה',
  'auth.google': 'התחברות עם Google',
  'auth.signedInAs': 'מחוברים כ{name}',
  'auth.signOut': 'התנתקות',
  'auth.rename': 'שינוי שם',
  'auth.renameLabel': 'איך קוראים לכם כאן',
  'auth.renameSave': 'שמירה',
  'auth.renameCancel': 'ביטול',
  'auth.renameFailed': 'לא הצלחנו לשמור את השם.',
  'auth.erase': 'מחיקת החשבון שלי',
  'auth.eraseWhat': 'הפעולה מוחקת את הפוסטים שלכם, את הקהילות שאתם מנהלים ואת הלימודים שהבטחתם. אי אפשר לבטל אותה.',
  'auth.eraseYes': 'למחוק הכול',
  'auth.eraseCancel': 'להשאיר את החשבון',
  'auth.eraseFailed': 'זה לא עבד — שום דבר לא נמחק.',
  'auth.signInDidNotFinish': 'ההתחברות לא הושלמה, ולכן שום דבר לא השתנה. הקישו כדי לסגור.',
  'rooms.micDidNotOpen': 'המיקרופון לא נפתח — לחצו שוב.',
  'rooms.micStillOn': 'המיקרופון עדיין פועל — לא הצלחנו לכבות אותו.',
  'auth.sessionEnded': 'ההתחברות שלכם הסתיימה, ולכן זה לא קרה. רעננו את הדף כדי להתחבר שוב.',
  'rooms.micSilent': 'המיקרופון פתוח ולא נשמע ממנו דבר — בדקו את המכשיר ולחצו כדי לנסות שוב.',
  'rooms.notPlaying': 'הדפדפן לא משמיע את החדר — לחצו כאן כדי לשמוע',
  'rooms.following': 'עוקבים אחרי החדר',
  'rooms.followAgain': 'לעקוב שוב אחרי החדר',
  'rooms.micBlocked': 'הדפדפן חוסם את המיקרופון באתר הזה, וחדר לא ייפתח בלעדיו. אפשרו אותו ונסו שוב.',
  'rooms.yoursHeading': 'חדרים שפתחתם',
  'rooms.yoursClose': 'סגירה',
  'rooms.yoursCloseNamed': 'סגירת {title}',
  'chat.notSent': 'ההודעה לא נשלחה — המילים חזרו לתיבה, נסו שוב.',
  'halls.addressFromName': 'לא הצלחנו ליצור כתובת מהשם הזה — הקלידו כתובת למטה, באותיות לטיניות ובספרות.',
  'refusal.suspended': 'החשבון הזה הושעה.',
  'refusal.roomFull': 'החדר מלא ({capacity}) — נסו שוב עוד רגע, או פתחו חדר אחר.',
  'refusal.stageFull': 'הבמה מלאה ({limit}).',
  'refusal.tooFast': 'יותר מדי בקשות בבת אחת — המתינו רגע ונסו שוב.',
  'refusal.addressTaken': 'הכתובת הזאת תפוסה.',
  'refusal.tooManyRooms': 'סגרו אחד מ־{count} החדרים הפתוחים שלכם לפני שתפתחו עוד אחד.',
  'refusal.tooManyHalls': 'אתם כבר מנהלים {count} בתים.',
  'refusal.tooManyCoHosts': 'לבית יכולים להיות {limit} מנחי־משנה — הורידו אחד קודם.',
  'refusal.tooManySessions': 'בטלו אחד מ־{count} הלימודים שקבעתם לפני שתוסיפו עוד אחד.',
  'refusal.tooManyChannels': 'נתקו אחד מ־{count} הערוצים שלכם לפני שתחברו עוד אחד.',
  'refusal.hallIsFull': 'בבית הזה {limit} חברים — מישהו צריך לעזוב לפני שאפשר לצרף עוד אחד.',
  'refusal.notAName': 'זה לא שם.',
  'refusal.notALink': '{url} הוא לא קישור.',
  'refusal.addressNotUsable': 'הכתובת הזאת לא שמישה — אותיות קטנות באנגלית, ספרות ומקפים, ולא מילה שמורה.',
  'refusal.notADiscordWebhook': 'זאת לא כתובת webhook של ערוץ בדיסקורד.',
  'refusal.notAYouTubeChannel': 'זאת לא כתובת של ערוץ ביוטיוב.',
  'refusal.postIsGone': 'הרשומה הזאת כבר לא שם — ייתכן שהוסרה.',
  'refusal.voiceUnreachable': 'שירות הקול לא זמין כרגע — החדר עדיין כאן, אז נסו שוב עוד רגע.',
  'refusal.silencedButNotMuted': 'החדר מוגדר כך שרק מנחים מדברים, וכל מי שנכנס מעכשיו נכנס להאזנה — אבל לא הצלחנו להגיע לשירות הקול כדי להשתיק את מי שכבר מדבר, אז נסו שוב עוד רגע.',
  'refusal.nobodyMuted': 'לא הצלחנו להגיע לשירות הקול, ולכן לא הושתק אף אחד — נסו שוב עוד רגע.',
  'refusal.stillInTheRoom': 'לא הצלחנו להגיע לשירות הקול, ולכן הם עדיין בחדר — נסו שוב עוד רגע.',
  'refusal.stageNotLive': 'הבמה עודכנה, אבל לא הצלחנו להגיע לשירות הקול — השינוי ייכנס לתוקף כשיצטרפו מחדש.',


  'hint.text': 'הקישו על שם כדי לפתוח אותו — ואז עקבו אחר הקישורים בכל פסוק כדי לנוע בין הכתובים.',
  'hint.dark': 'החושך הוא מה שטרם סקרתם',
  'hint.zoomIn': 'התקרבו כדי לקרוא את השמות',
  'hint.tap': 'הקישו על שם כדי לפתוח את פסוקיו',
  'hint.flying': 'esc ליציאה · גררו כדי להמשיך לעוף',
  'hint.dismiss': 'סגירה',

  'type.person': 'אדם',
  'type.place': 'מקום',
  'type.people': 'עם',
  'type.divine': 'האלוהי',
  'type.unknown': 'לא מסווג',
  'type.object': 'חפץ',
  'type.observance': 'מועד',
  'type.institution': 'מוסד',
  'type.scripture': 'ספר',
  'type.angelic': 'מלאך או רוח',
  'type.deity': 'אֵל',
  'type.time': 'זמן',
  'type.language': 'שפה',
  'type.practice': 'מנהג',
  'type.ruling': 'הלכה',
  'type.event': 'אירוע',
  'type.realm': 'עולם',
  'type.attribute': 'שם או תואר',
  'type.religion': 'דת',
  'type.tribe': 'שבט',
  'type.celestial': 'קבוצת כוכבים',
  'type.concept': 'מושג',
  'type.parable': 'משל',
  'type.prayer': 'תפילה',
  'type.creature': 'יְצוּר',

  'panel.about': 'אודות {name}',
  'panel.openDiscussion': 'פתיחת דיון',
  'panel.namedIn': 'מוזכר ב',
  'panel.reachAll': '{name} מוזכר בכולם.',
  'panel.reachSome': '{name} מוזכר ב-{named} מתוך {total} הכתבים.',
  'panel.reachOnly': '{name} מוזכר רק ב{text}.',
  'panel.onlyThis': 'הצג רק את {text}',
  'panel.trails': 'נתיבים מכאן',
  'panel.inward': 'פנימה',
  'panel.outward': 'החוצה',
  'panel.linkedBy': 'מקושר דרך {ref}',
  'panel.sharesName': 'דמות נוספת נושאת את השם הזה:',
  'panel.identity': 'שמות שיש הטוענים כי הם ישות אחת:',
  'panel.argued': 'נתון במחלוקת, בפסוקים הבאים:',
  'panel.identityHeld': 'הטוענים',
  'panel.identityDisputed': 'החולקים',
  'unit.passage': 'קטע',
  'unit.passages': 'קטעים',
  'panel.contested': 'קיימת מחלוקת אם זו דמות אחת לאורך הכתובים.',
  'panel.shared': 'קטעים משותפים',
  'panel.reports': 'מסופר גם על {name}',
  'panel.reportsArabicOnly': 'רובם בערבית בלבד. HadeethEnc מתירה את התרגום שלה לאנגלית והוא מוצג בכל מקום שבו הוא תואם דיווח כאן — אבל זו מבחר של אנציקלופדיה סונית, ולכן הוא מגיע לאוספים הסוניים ולא לאף אחד מהאוספים התריסריים, הזיידיים או האבאדיים. במקומות אחרים קיימים תרגומים לאנגלית והם מוגנים בזכויות יוצרים, והספרייה הזו לא קיבלה רשות להשתמש בהם.',
  'panel.reportsEnglishFrom': 'תרגום לאנגלית מאת HadeethEnc',
  'panel.reportsCut': 'הדיווח הזה קטוע — המקור שממנו הוא לקוח נעצר כאן, באמצע מילה.',
  'panel.reportsCutTitle': 'הקובץ של הספק מגביל כל דיווח ל‑200 תווים. שאר הנוסח מעולם לא נמסר, והוא אינו חסר באוסף עצמו.',
  'panel.reportsNumbering': 'כשמראה־מקום כאן הוא שם ומספר בלבד, זהו המספור של מי שהקליד את הטקסט ולא של מהדורה מודפסת, והוא לא יתאים לציטוט ממקום אחר.',
  'panel.reportsChain': 'שרשרת המסירה',
  'panel.reportsChainWho': 'השורה שמתחת לשם היא מי שהמאגר אומר שאותו אדם היה, ומתי מת — שרשרת מסירה נוקבת בשם הקצר שזיהה אותו בפני שומעיו. השם שמעליה הוא מה שהספר עצמו מדפיס.',
  'panel.reportsCites': 'מצטט',
  'panel.reportsAlso': 'מזכיר גם',
  'panel.reportsAgreed': 'גם ב',
  'panel.reportsAgreedTitle': 'שני הקבצים מוסרים את הדיווח הזה — מֻתַּפַק עליה, מוסכם',
  'panel.reportsPlaceTitle': 'מיקום הדיווח בקובץ — הספר והפרק של המחבר עצמו, שאותרו במהדורה מודפסת מזוהה',
  'panel.reportsPlaceInferredTitle': 'הספר משוער מן הדיווחים שמשני צדיו, המצויים בו; הפרק אינו ידוע',
  'panel.reportsQuotedFrom': 'מצוטט מתוך',
  'panel.reportsQuotedFromTitle': 'החיבור הקדום שממנו נלקח הדיווח. מֻסְתַדְרַכּ אל-וַסאאִל הוא השלמה: אל-נורי אסף את שהושמט, ולכן הדיווחים קדומים מן הספר שנושא אותם.',
  'panel.reportsGraded': '{who} שפט אותו',
  'panel.reportsGradedTitle': 'פסיקת המחבר עצמו על הדיווח שהביא, בלשונו — לא של הספרייה הזאת ולא של עורך מודרני. דירוג הוא דעתו של חכם אחד על דיווח, ולכן הוא מוצג עם שמו: מנותק ממנו אין לו משמעות.',
  'panel.gradeHasanSahih': 'חסן צחיח — טוב ומהימן',
  'panel.gradeHasanGharib': 'חסן ע׳ריב — טוב, ונמסר בשרשרת אחת בלבד',
  'panel.gradeSahih': 'צחיח — מהימן',
  'panel.gradeHasan': 'חסן — טוב',
  'panel.gradeGharib': 'ע׳ריב — נמסר בשרשרת אחת בלבד',
  'panel.gradeDaif': 'צ׳עיף — חלש',
  'panel.gradeMunkar': 'מונכר — נדחה',
  'panel.gradeMursal': 'מורסל — השרשרת נעצרת לפני הנביא',
  'panel.gradeMudtarib': 'מוצ׳טריב — השרשרות סותרות זו את זו',
  'panel.gradeMukhtasar': 'מוח׳תצר — קיצור של דיווח ארוך יותר',
  'panel.gradeMansukh': 'מנסוח׳ — בוטל בהוראה מאוחרת',
  'panel.gradeMalul': 'מעלול — נושא פגם נסתר',
  'panel.branchSunni': 'סוני',
  'panel.branchTwelver': 'שיעה תרי-עשר',
  'panel.branchZaydi': 'שיעה זיידית',
  'panel.branchIbadi': 'אבאדי',
  'panel.branchTitle': 'איזה ענף באסלאם מחזיק באוסף שהדיווח הזה שייך לו. הספרייה נושאת מדפים מארבעה ענפים, ודף של דמות מציג אותם יחד.',
  'panel.layerMarfu': 'מן הנביא',
  'panel.layerQudsi': 'דברי האל, מפי הנביא',
  'panel.layerMawquf': 'נעצר אצל בן־לוויה',
  'panel.layerMaqtu': 'נעצר אצל דור הממשיכים',
  'panel.reportsSplit': '{named} מזכירים את הדמות · {narrated} נמסרו על ידה',
  'panel.reportsNarrated': 'נמסר על ידי {name}',
  'panel.sharedLead': 'אותו אירוע, מסופר בתנ״ך ובקוראן — לקריאה זה לצד זה.',
  'panel.differLead': 'היכן שהתיאור של דמות זו נבדל בין התנ״ך לקוראן — קראו את שניהם, כשאלה, לעולם לא כהכרעה.',
  'panel.filterPlaceholder': 'סינון — טקסט, ספר, או 1:1',
  'panel.filterLabel': 'סינון הקטעים של {name}',
  'panel.chipsLabel': 'הצגה או הסתרה של כתוב',
  'panel.noMatch': 'אין קטעים תואמים.',
  'panel.showing': 'מוצגים {shown} מתוך {total}',
  'panel.sortLabel': 'מיון',
  'panel.sortInOrder': 'לפי הסדר',
  'panel.sortLeadFirst': '{name} קודם',
  'panel.noPassages': 'אין כאן פסוקים מקושרים — הכתובים מצביעים על דמות זו בתיאור, או בשם נפוץ מכדי לסמן פסוק־פסוק, ולכן אין רשימת קטעים לפתוח.',
  'panel.noPassagesReportsOnly': 'לא המקרא ולא הקוראן מזכירים דמות זו. היא כאן משום שהחדית׳ים מזכירים אותה — מה שנמסר עליה מופיע למעלה.',

  'verses.loading': 'פותח את הקטעים…',
  'verses.loadingCount': 'פותח {count} קטעים…',
  'verses.heading': 'קטעים',
  'verses.orderEach': 'מסודרים אחד מכל אחד, בתורו.',
  'verses.orderFrom': 'מסודרים החל מ{text}, ואז אחד מכל אחד בתורו.',
  'verses.notLoaded': 'הקטע הזה לא נטען.',
  'verses.otherTranslations': 'עוד {n}',
  'verses.nonCommercialTitle': 'רישיון התרגום הזה אוסר למכור אותו.',
  'verses.nonCommercial': 'לא מסחרי',
  'verses.hebrewNumbering': 'עברי',
  'verses.hebrewNumberingWhy': 'ההפניות כאן לפי מספור המלך ג׳יימס. במקרא העברי הפרק הזה ממוספר אחרת.',
  'verses.noOriginal': 'ל{edition} אין טקסט בהפניה הזאת.',
  'verses.readOn': 'קריאה ב-{source}',
  'verses.discuss': 'דיון על הפסוק הזה',
  'verses.incomplete': 'חלק מהקטעים לא נטענו — הקישורים למעלה פותחים אותם במקום אחר.',

  'translate.hide': 'הסתרת תרגום אוטומטי',
  'translate.to': 'תרגום אוטומטי ל{lang}',
  'translate.inProgress': 'מתרגם…',
  'translate.caveat': 'אוטומטי — קריאה מכנית גסה, לא תרגום מוסמך.',
  'translate.unsupported': 'אין תרגום מכשירי ל{lang} בדפדפן הזה.',
  'translate.error': 'לא ניתן היה לתרגם את הפסוק הזה.',

  'discussion.edit': 'עריכה',
  'discussion.editing': 'תיקון הדברים',
  'discussion.save': 'שמירה',
  'discussion.cancelEdit': 'ביטול',
  'discussion.edited': '(נערך)',
  'discussion.report': 'דיווח',
  'discussion.reported': 'דווח',
  'discussion.remove': 'הסרה',
  'discussion.postError': 'הפרסום נכשל — נסו שוב.',
  'discussion.label': 'דיון',
  'discussion.add': 'הוספה לדיון',
  'discussion.placeholder': 'הוספה לדיון…',
  'discussion.post': 'פרסום',
  'discussion.toTakePart': ' כדי להשתתף.',
  'discussion.loading': 'טוען את הדיון…',
  'discussion.earlier': 'הודעות קודמות',
  'discussion.earlierFailed': 'לא הצלחנו לטעון הודעות קודמות.',
  'discussion.empty': 'עדיין אף אחד לא כתב כאן.',
  'discussionDrawer.label': 'דיון — {title}',
  'discussionDrawer.heading': '{title} — דיון',

  'voice.label': 'חדר קולי',
  'voice.heading': 'קול',
  'voice.leave': 'יציאה מהשיחה',
  'voice.toJoin': ' כדי להצטרף.',

  'reports.heading': 'פוסטים שדווחו',
  'reports.failed': 'זה לא עבר — שום דבר לא השתנה. נסו שוב.',
  'reports.keep': 'להשאיר',
  'reports.remove': 'להסיר',
  'reports.times': 'דווח {count} פעמים',
  'reports.departed': 'קורא שעזב',
  'reports.more': 'ועוד {count} ממתינים',
  'reports.suspend': 'להשעות את {name}',
  'reports.reinstate': 'להחזיר',
  'reports.history': 'מה עשו המנחים',
  'reports.earlier': 'קודם לכן',
  'reports.wasSuspended': 'הושעה בידי',
  'reports.wasSomething': 'טופל בידי',
  'reports.wasReinstated': 'הוחזר בידי',
  'rooms.empty': 'עדיין אף אחד לא מדבר כאן.',
  'rooms.openOne': 'פתיחת חדר',
  'rooms.whoMayCome': 'מי יכול להיכנס',
  'rooms.accessPublic': 'כל אחד',
  'rooms.accessPassphrase': 'עם סיסמה',
  'rooms.accessInvite': 'בהזמנה בלבד',
  'rooms.setPassphrase': 'הגדרת סיסמה',
  'rooms.invitePlaceholder': 'הדביקו קוד הזמנה',
  'rooms.useInvite': 'שימוש',
  'rooms.shareInvite': 'שלחו לאנשים את הקוד הזה: ',
  'rooms.underHall': 'פתיחה בשם',
  'rooms.commons': 'כולם',
  'halls.couldNotReadYours': 'לא הצלחנו לקרוא את הקהילות שלך כרגע, ולכן זה לא ייפתח תחת אף אחת מהן.',
  'rooms.titlePlaceholder': 'על מה החדר הזה?',
  'rooms.opening': 'פותח…',
  'rooms.roomLabel': 'הצטרפות אל ״{title}״',
  'rooms.inRoom': '{count} כאן',
  'rooms.nobodyYet': 'ריק',
  'rooms.full': 'מלא ({count})',
  'rooms.barred': 'החדר הזה סגור בפניכם.',
  'rooms.notHost': 'רק מנחה יכול לעשות זאת.',
  'rooms.close': 'סגירת החדר',
  'rooms.barredHeading': 'מנועים מהחדר הזה',
  'rooms.letBackIn': 'להחזיר את {name}',
  'rooms.departedReader': 'קורא שעזב',
  'rooms.lockDoor': 'נעילת הדלת',
  'rooms.openDoor': 'לפתוח לכולם',
  'rooms.newCode': 'קוד חדש',
  'rooms.silence': 'השתקת החדר',
  'rooms.hostBadge': 'מנחה',
  'rooms.listening': 'מאזין',
  'rooms.unheard': 'לא מגיע אליכם',
  'rooms.muteSelf': 'השתקה שלי',
  'rooms.unmuteSelf': 'ביטול ההשתקה שלי',
  'rooms.participants': 'בחדר הזה',
  'rooms.stageSeats': '{taken} מתוך {limit} על הבמה',
  'rooms.showEveryone': 'להציג את שאר {count} הנוכחים',
  'rooms.moreAsking': 'עוד {count} ממתינים',
  'rooms.forceMute': 'השתקת {name}',
  'rooms.hushForMe': 'להשתיק את {name} רק אצלי',
  'rooms.hearAgain': 'לשמוע שוב את {name}',
  'rooms.toStage': 'לאפשר ל{name} לדבר',
  'rooms.offStage': 'להפסיק את הדיבור של {name}',
  'rooms.kick': 'הסרת {name}',
  'rooms.ban': 'חסימת {name}',
  'rooms.makeHost': 'מינוי {name} כמארח',
  'rooms.deskOpen': 'לפתוח חדר, או להשתמש בהזמנה',
  'rooms.talkHere': 'לדבר על זה',
  'rooms.locked': 'דורש סיסמה',
  'rooms.passphrasePlaceholder': 'סיסמה',
  'rooms.enter': 'כניסה',
  'rooms.back': 'חזרה',
  'rooms.wrongPassphrase': 'הסיסמה הזו לא פותחת את החדר.',
  'rooms.wentWrong': 'זה לא עבד כרגע. שום דבר לא אבד — נסו שוב בעוד רגע.',
  'rooms.reconnecting': 'החיבור נקטע — מנסים לחזור לחדר…',
  'rooms.handUp': 'לבקש לדבר',
  'rooms.handDown': 'לא צריך',
  'rooms.asking': '{count} מבקשים לדבר',
  'rooms.notNow': 'לא עכשיו, {name}',
  'rooms.speakNext': 'לתת ל{name} לדבר עכשיו',
  'rooms.coHostAsked': 'ביקשתם מקהילה אחרת לארח יחד. ',
  'rooms.coHosting': 'מתארח יחד עם קהילה אחרת. ',
  'rooms.coHostEnd': 'לסיים',
  'rooms.coHostAsk': 'להזמין לארח יחד',
  'rooms.coHostPlaceholder': 'הכתובת של הקהילה',
  'rooms.coHostNoSuchHall': 'אין קהילה בכתובת הזו.',
  'rooms.removedYou': 'מנחה הסיר אתכם מהחדר הזה.',
  'rooms.hostClosed': 'המנחה סגר את החדר הזה.',
  'rooms.joinedElsewhere': 'הצטרפתם לחדר הזה ממקום אחר, אז החיבור כאן נסגר.',
  'rooms.connectionLost': 'החיבור לחדר הזה אבד.',

  'chat.label': 'צ׳אט בחדר',
  'chat.heading': 'צ׳אט',
  'chat.empty': 'לא נאמר דבר מאז שהגעתם.',
  'chat.placeholder': 'כתבו משהו…',
  'chat.send': 'שליחה',

  'sessions.label': 'מפגשים מתוזמנים',
  'sessions.heading': 'בקרוב',
  'sessions.empty': 'עדיין לא תוזמן כאן דבר.',
  'sessions.weekly': 'כל שבוע',
  'sessions.joinNow': 'הצטרפות עכשיו',
  'shared.timeUnknown': 'זמן לא ידוע',
  'sessions.underHall': 'קיום בשם',
  'sessions.cancel': 'ביטול',
  'sessions.yours': 'המפגשים שלכם במקומות אחרים',
  'sessions.goThere': 'מעבר אל {name}',
  'sessions.cancelThis': 'ביטול {name}',
  'sessions.whenLabel': 'מתי',
  'sessions.titlePlaceholder': 'מה תלמדו?',
  'sessions.everyWeek': 'כל שבוע',
  'sessions.startsIn': 'בעוד {count} דקות',
  'sessions.lasts': 'למשך {count} דקות',
  'sessions.startingNow': 'מתחיל עכשיו',
  'sessions.howLong': 'משך המפגש',
  'sessions.minutes': '{count} דקות',
  'sessions.schedule': 'תזמון',
  'sessions.scheduling': 'מתזמן…',
  'sessions.toSchedule': ' כדי לתזמן מפגש.',
  'sessions.badTime': 'זה לא זמן תקין.',
  'sessions.failed': 'זה לא עבד — נסו שוב.',
  'sessions.newPassphrase': 'סיסמה חדשה',
  'sessions.changeIt': 'החליפו',
  'sessions.rekeyThis': 'החלפת הסיסמה של {name}',
  'sessions.whoMayCome': 'מי יוכל להשתתף במפגש',
  'sessions.setPassphrase': 'סיסמה למפגש',
  'sessions.calledOff': 'בוטל',
  'sessions.hallGone': 'הקהילה שתחתיה התקיים נמחקה',
  'sessions.notPermitted': 'אינכם יכולים עוד לקיים מפגשים תחת אותה קהילה',

  'channels.heading': 'הודעה לדיסקורד',
  'channels.connect': 'חיבור',
  'channels.connecting': 'מתחבר…',
  'channels.urlLabel': 'כתובת Webhook של דיסקורד',
  'channels.urlHelp': 'בדיסקורד: הגדרות ערוץ ← אינטגרציות ← Webhooks ← העתקת כתובת.',
  'channels.thisPassage': 'כשנפתח חדר בקטע הזה',
  'channels.everyPassage': 'כשנפתח חדר בכל מקום',
  'channels.followsThis': 'מודיע על חדרים בקטע הזה',
  'channels.followsAll': 'מודיע על חדרים בכל מקום',
  'channels.retired': 'הופסק — חברו מחדש',
  'channels.retiredDeleted': 'הופסק — הוובהוק הזה כבר לא קיים בדיסקורד. צרו חדש וחברו אותו.',
  'channels.retiredRefused': 'הופסק — דיסקורד סירב. בדקו את ההרשאות של הערוץ ואז חברו מחדש.',
  'channels.followsSome': 'מודיע על {count} קטעים',
  'channels.disconnect': 'ניתוק',
  'channels.reconnect': 'לחבר מחדש',
  'channels.none': 'אין ערוץ מחובר.',
  'channels.failed': 'זה לא עבד — נסו שוב.',

  'hall.label': 'היכל',
  'hall.loading': 'פותח את ההיכל…',
  'hall.missing': 'אין היכל בכתובת הזו.',
  'hall.unreadable': 'לא הצלחנו לקרוא את הקהילה הזו כרגע — היא עדיין שם, נסו שוב עוד רגע.',
  'hall.standingUnknown': 'לא הצלחנו לדעת אם אתם בקהילה הזו כרגע — נסו שוב עוד רגע.',
  'hall.back': 'חזרה למפה',
  'hall.verifiedMark': '✓ מאומת',
  'hall.verifiedAs': 'הוכחה שליטה ב{handle}',
  'hall.join': 'הצטרפות להיכל',
  'hall.leave': 'עזיבה',
  'hall.settings': 'עמוד הבית של ההיכל',
  'hall.nameLabel': 'שם',
  'hall.descriptionLabel': 'למה הוא נועד',
  'hall.linksLabel': 'קישורים החוצה',
  'hall.linkKind': 'סוג הקישור',
  'hall.linkUrl': 'כתובת',
  'hall.linkLabel': 'איך לקרוא לו',
  'hall.addLink': 'הוספת קישור',
  'hall.dropLink': 'הסרה',
  'hall.doorLabel': 'מי יכול להצטרף',
  'hall.doorOpen': 'כל אחד יכול להצטרף',
  'hall.doorApproval': 'אתם מאשרים כל אדם',
  'hall.listingLabel': 'בספרייה',
  'hall.listingPublic': 'מופיע',
  'hall.listingUnlisted': 'בקישור בלבד',
  'hall.save': 'שמירה',
  'hall.saving': 'שומר…',
  'hall.team': 'מי עוזר לכם לנהל',
  'shared.couldNotRead': 'לא הצלחנו לקרוא את זה כרגע — זה לא ריק, נסו שוב עוד רגע.',
  'hall.bar': 'לחסום את {name}',
  'hall.barred': 'חסומים מהקהילה',
  'hall.letBackIn': 'להחזיר את {name}',
  'hall.coHost': 'מנחה שותף',
  'hall.makeCoHost': 'מינוי כמנחה שותף',
  'hall.standDown': 'סיום תפקיד',
  'hall.departed': 'קורא שעזב',
  'hall.log': 'מה עשו המנחים שלכם',
  'hall.logEarlier': 'קודם לכן',
  'hall.showMore': 'הצגת עוד',
  'hall.invited': 'הוזמנתם לארח יחד',
  'hall.acceptCoHost': 'לארח יחד',
  'hall.declineCoHost': 'לא, תודה',
  'log.mute': 'השתיק את {name}',
  'log.onStage': 'נתן ל{name} לדבר',
  'log.offStage': 'הוריד את {name} מהבמה',
  'log.remove': 'הסיר את {name}',
  'log.ban': 'חסם את {name}',
  'log.unban': 'הסיר את החסימה מ{name}',
  'log.addHost': 'מינה את {name} למנחה',
  'log.silence': 'השתיק את החדר',
  'log.closeRoom': 'סגר את החדר',
  'log.doorLocked': 'נעל את הדלת',
  'log.doorOpened': 'פתח את הדלת לכולם',
  'log.unnamed': 'פעל כלפי {name} — הדף הזה ישן מן הרשומה',
  'log.moderatorAdded': 'מינה את {name} למנחה שותף',
  'log.moderatorRemoved': 'סיים את תפקידו של {name} כמנחה שותף',
  'log.hallBanned': 'חסם את {name} מהקהילה',
  'log.hallUnbanned': 'החזיר את {name} לקהילה',
  'log.coHostAsked': 'הזמין קהילה אחרת לארח יחד',
  'log.coHostAccepted': 'הסכים לארח יחד חדר של קהילה אחרת',
  'log.coHostEnded': 'סיים אירוח משותף',
  'hall.waiting': 'ממתינים להצטרף',
  'hall.waitingToBeLetIn': 'ביקשתם להצטרף. אחד המנחים יאשר.',
  'hall.withdraw': 'ביטול הבקשה',
  'hall.approve': 'אישור כניסה',
  'hall.asked': 'ביקשו {when}',
  'hall.talkingNow': 'מדברים עכשיו',
  'hall.comingUp': 'בקרוב',
  'hall.roomHere': '{count} כאן',
  'hall.roomQuiet': 'נפתח זה עתה',
  'hall.cannotJoin': 'אינכם יכולים להצטרף להיכל הזה.',
  'hall.ownerOnly': 'רק הבעלים יכול לעשות זאת.',
  'hall.ownerTools': 'שלכם לניהול',
  'hall.delete': 'מחיקת ההיכל',
  'hall.deleteConfirm': 'הקלידו {slug} לאישור',
  'hall.deleteFinal': 'למחוק, לתמיד',
  'hall.deleteCancel': 'להשאיר',
  'hall.export': 'הורדת הכול',
  'hall.verify': 'הוכחה שההיכל שלכם',
  'hall.handlePlaceholder': 'ערוץ היוטיוב שלכם',
  'hall.challengeLead': 'הדביקו את זה בתיאור של {handle}, ואז בדקו.',
  'hall.checkNow': 'בדיקה עכשיו',
  'hall.toTakePart': ' כדי להשתתף.',
  'hall.failed': 'זה לא עבד — נסו שוב.',
  'halls.label': 'היכלים',
  'halls.heading': 'היכלים',
  'halls.yours': 'שלכם',
  'halls.public': 'פתוחים לכולם',
  'halls.none': 'עדיין אין היכלים.',
  'halls.noneYours': 'עדיין אינכם בהיכל.',
  'halls.startOne': 'פתיחת היכל',
  'halls.listInDirectory': 'שיופיע במקום שבו אפשר למצוא אותו',
  'hall.memberCount': '{count} חברים',
  'hall.memberOfLimit': '{count} מתוך {limit} חברים',
  'halls.opening': 'פותח…',
  'halls.namePlaceholder': 'איך נקראת הקהילה שלכם?',
  'halls.addressPlaceholder': 'כתובת',
  'halls.toStart': ' כדי לפתוח היכל.',
  'halls.failed': 'זה לא עבד — נסו שוב.',
  'halls.browse': 'היכלים',

  'reader.label': 'קורא {unit}',
  'reader.labelFallback': 'קריאת הקטע',
  'reader.heading': 'קטע',
  'reader.alsoTold': 'מסופר גם ב{ref}',
  'reader.alsoCounted': 'נספר אחרת ב{ref}',

  'story.sharedTitle': '{figure} — אירוע אחד, שני כתובים',
  'story.sharedCaption': 'הוצמדו כי שניהם משתמשים ב: {shared}. הצעה לקרוא את שניהם, לא טענה שהם אומרים אותו דבר.',
  'story.toldTitle': '{group} — מסופר פעמיים',
  'story.toldCaption': '{count} פסוקים משותפים — שני סיפורים בתוך כתוב אחד, מוצגים זה לצד זה, ללא שיפוט.',

  'told.lead': 'אותו פרק, מסופר פעמיים בתוך כתוב אחד — מוצג זה לצד זה, ללא שיפוט.',
  'told.shared': '{count} משותפים',

  'differ.lead': 'מקומות שבהם התנ״ך והקוראן מספרים אירוע אחד באופן שונה — מוצג כשאלה עם שתי הקריאות, לא כהכרעה.',
  'numbers.lead': 'מקומות שבהם כתוב אחד נותן שני מספרים שונים לאותו דבר — עם ההסתייגות שאותה מילה יכולה למנות דברים שונים.',
  'differences.readBoth': 'קריאת שני התיאורים',

  'disputed.badgeSameName': 'אותו שם, כנראה שונים',
  'disputed.badgeDisputed': 'זהות שנויה במחלוקת',
  'disputed.heading': 'זהויות שנויות במחלוקת',
  'disputed.lead': 'דמויות שהמסורות מזהות — או לא — באותו אופן. האם זו דמות אחת לאורך הכתובים, או שתיים החולקות שם? קראו את המקרה, ואז הביעו דעה. מוצג כשאלה פתוחה, לעולם לא כהכרעה.',
  'disputed.weighIn': 'הבעת דעה',
  'disputed.seeOnMap': 'הצגת {name} על המפה',
  'contested.passagesHeading': 'קטעים שמתווכחים עליהם',
  'contested.passagesLead': 'נמדד, לא נבחר: אלה הקטעים שיותר ממסורת אחת נשמעה מתווכחת עליהם. פִּתחו וקִראו.',
  'contested.tradition.christian': 'נוצרי',
  'contested.tradition.muslim': 'מוסלמי',
  'contested.tradition.jewish': 'יהודי',
  'contested.open': 'היכן חלוקים',
  'contested.alongside': 'נדון יחד עם',

  'sources.lead': 'כל תרגום מצוין בשמו, ומה מוחזק בחוץ ולמה.',
  'sources.labelEnglish': 'אנגלית',
  'sources.labelSecond': 'אנגלית שנייה',
  'sources.labelThird': 'אנגלית שלישית',
  'sources.labelAlso': 'גם ב',
  'sources.renderingGate': 'כל אחד מאלה מוצג רק היכן שניתן להתאים אותו פסוק מול פסוק, ומושמט במקום להיות מוצג מול הפסוק הלא נכון. לרוב הסיבה היא חלוקת הפרקים: תרגום שמחלק פסוקים אחרת אינו ניתן ליישור. היכן שתרגום חסר מסיבה אחרת — קאנון שמעולם לא כלל את הספרים האלה, תעתיק עם פערים — ההערה שלו מציינת מהי.',
  'sources.labelOriginal': 'מקור',
  'sources.noEnglish': 'אין תרגום אנגלי מפורסם — נקרא בערבית, או כקריאה אוטומטית מסומנת',
  'sources.heldBack': 'מוחזק בחוץ',
  'sources.heldBackAffects': 'נוגע ל',
  'sources.absent': 'זרמים שאינם מיוצגים כאן',
  'sources.absentLead': 'אף אחת מן המסורות האלה אינה מיוצגת בספרייה הזו עדיין. בכל המקרים מדובר בבעיית רישוי או דיגיטציה מצידנו, ולעולם לא באמירה על הטקסטים עצמם.',
  'sources.absentSplit': 'מתוך {total}, {awaited} נעדרות לגמרי — איש לא נתן רשות. לגבי {partly} האחרות, חלק מן הטקסט כבר נמצא כאן ועדיין אי אפשר להציגו כפי שקורא של אותו זרם היה זקוק לו.',
  'sources.absentHolds': 'הקוראים יחפשו כאן',
  'sources.unshelved': 'נמצאים כאן, טרם שובצו',
  'sources.unshelvedLead': 'הטקסטים האלה נמצאים כאן ומותרים לפרסום. {count} קטעים ממתינים להחלטה היכן מקומם — העיכוב הוא שלנו.',
  'sources.unshelvedPassages': 'קטעים שמורים',
  'sources.blockedPermission': 'ממתין לאישור',
  'sources.blockedOriginal': 'אין מקור שניתן לפרסם',
  'sources.blockedAddressing': 'אינו מחולק לפסוקים',
  'sources.blockedTranslation': 'אין תרגום שפורסם',
  'sources.blockedTranscription': 'חופשי, אך טרם תועתק במדויק',
  'sources.expiries': 'יגיעו בתאריך ידוע',
  'sources.expiriesLead': 'אינם חסרים לתמיד — הם מוגנים בזכויות יוצרים עד תום התקופה, והתאריך ידוע.',
  'sources.freeFrom': 'חופשי ב־{date}',
  'sources.expiryMonths': 'בעוד כ־{n} חודשים',
  'sources.expiryYears': 'בעוד כ־{n} שנים',
  'sources.heldAsScripture': 'נחשב כתב קודש אצל',
  'sources.notHeldAsScripture': 'ולא אצל',
  'sources.alsoHeldBy': 'נחשב כתב קודש גם אצל',
  'sources.notInCanonOf': 'אינם נכללים בקאנון של',
  'sources.heldBackLead': 'לא נכלל — שאלה של רישיון, לא של עריכה.',
  'sources.awaiting': 'ממתין ל{name}',

  'search.label': 'חיפוש שם על המפה',
  'search.placeholder': 'חיפוש שם — אברהם, מוסא…',
  'search.nothing': 'אין כאן שם שמתאים ל“{term}”.',
  'search.openPassage': 'פתח {ref} ←',

  'offline.saving': 'שומר לשימוש לא מקוון…',
  'offline.savingCount': 'שומר לשימוש לא מקוון… {done} מתוך {total}',
  'offline.save': 'שמירה לשימוש לא מקוון',
  'offline.saved': 'נשמרו {saved} מתוך {total} קבצים לקריאה לא מקוונת.',
  'offline.error': 'לא ניתן היה לשמור לשימוש לא מקוון.',

  'legend.open': 'מקרא — על מה אני מסתכל?',
  'legend.scriptures': 'כתובים',
  'legend.shapes': 'צורות',
  'legend.people': 'עם או אומה',
  'legend.shapeBeings': 'אדם, או מי שסוגדים לו',
  'legend.shapeThings': 'מקום, חפץ או כתב קודש',
  'legend.shapeBodies': 'עם, מועד או מוסד משותף',
  'legend.noScripture': 'אפור — אף כתב אינו נוקב בשמו; מתואר ולא נקרא בשם, או נזכר רק בחדית׳',
  'legend.marks': 'סימנים',
  'legend.size': 'גדול יותר — נזכר ביותר פסוקים; העמומים משורטטים בקנה מידה קטן משלהם',
  'legend.gold': 'טבעת זהב — קיימת מחלוקת אם זו דמות אחת לאורך הכתובים; פִּתחו כדי לקרוא את המקרה ולהביע דעה',
  'legend.faint': 'עמום — נמצא מקריאת הטקסטים, לא נבדק ידנית',
};

const ar: Partial<Strings> = {
  'shell.loading': 'جارٍ فتح المكتبة…',
  'shell.error': 'تعذّر الوصول إلى المكتبة. {detail}',
  'shell.retry': 'حاول مرة أخرى',
  'shell.pages': 'اقرأها كصفحة بدلاً من ذلك',
  'shell.staleLink': 'طلب هذا الرابط شيئًا لا تحتويه هذه المكتبة.',
  'map.summary': '{names} أسماء · {texts} نصوص',
  'map.wedgeOnly': '{count} تُذكر هنا فقط',
  'map.wedgeCounts': '{held} اسمًا · {alone} هنا فقط',
  'map.hoverCorpora': '{count} كتب',
  'map.hoverJoin': ' و',
  'map.hud.fps': 'fps',
  'map.hud.ms': 'ms',
  'map.hud.cells': 'خلايا',
  'map.hud.sky': 'سماء',
  'map.hud.names': 'أسماء',
  'map.hud.disc': 'قرص',
  'map.hud.dot': 'نقطة',
  'map.hud.clump': 'كتلة',
  'map.hud.strands': 'خيوط',
  'map.hud.strandsOff': 'الخيوط متوقفة عند هذا المدى',
  'map.hud.chunks': 'أجزاء',
  'map.hud.kilobytes': 'KB',
  'map.hud.tilt': 'ميل {percent}%',
  'map.hud.zoom': 'z {zoom} من {floor}',
  'map.hud.labels': '{count} تسميات',
  'map.hud.fan': 'مروحة {strands}/{passages} مُستشهد',
  'map.hud.split': 'ثابت {still}ms ×{rate}/ث · حي {live}ms',
  'map.hud.layers': 'مسافرون {travellers} · مقاعد {seats} · قرّاء {readers} · غبار {dust} · متنازع {disputed}',
  'feed.exploring': '{count} يستكشفون',
  'feed.voice': 'صوت مباشر',
  'compare.open': 'قارن الروايات',
  'compare.disputed': 'شخصيات مُختلَف عليها',
  'compare.differ': 'حيث تختلف الروايات',
  'compare.numbers': 'أرقام غير متطابقة',
  'compare.toldTwice': 'مَروي مرّتين في كتاب واحد',
  'sources.open': 'المصادر والتراخيص',
  'lang.label': 'اللغة',

  'tier.0.name': 'أسماء الله',
  'tier.1.name': 'الغيب',
  'tier.2.name': 'من خوطبوا',
  'tier.3.name': 'المسمّون',
  'tier.4.name': 'الأمم',
  'tier.unknown': 'العالم المسمّى',

  'tour.size': '{names} اسمًا. {passages} مقطعًا.',
  'tour.size.sub': '{texts} كتب، تحملها {traditions} تقاليد. كل اسم منها يخصّ أحدًا.',
  'tour.centre': 'في المركز، أسماء الله.',
  'tour.centre.sub': 'كل تقليد يضع هناك شيئًا مختلفًا، وهذه الخريطة لا تختار.',
  'tour.rim': 'هنا تتوقف التسمية.',
  'tour.rim.sub': 'ليس العالم — بل ما سمّته هذه الكتب وحدها. والضوء وراءه هو كل ما لم تسمّه.',
  'tour.place': '{place}.',
  'tour.place.sub': '{reach} اسمًا تقف على بُعد خطوة واحدة من هنا.',
  'tour.landing': 'من هنا تبدأ.',
  'tour.landing.sub': 'يبقى الباقي مظلمًا حتى تذهب إليه.',
  'tour.skip': 'تخطَّ',

  'you.short': 'أنت',
  'you.open': 'أنت — كيف كنت تقرأ',
  'you.readingAs': 'كنت تقرأ بوصفك',
  'you.closestIn': 'الأقرب إلى الداخل',
  'you.furthestOut': 'الأبعد إلى الخارج',
  'you.nowhere': 'لم تفتح اسمًا بعد.',
  'you.atlas': 'أطلسك',
  'you.atlasRow': '{got} من {tot}',
  'travel.title': 'إلى أين؟',
  'travel.lead': 'عد إلى أي مكان مشيت فيه. الأماكن التي لم تزرها تُعرض من بعيد.',
  'travel.return': 'عودة',
  'travel.look': 'انطلق',
  'travel.walked': '{walked} من {total} استُكشفت',
  'travel.nowhere': 'لا توجد بعد مناطق للعودة إليها.',
  'standpoint.comparatist': 'المقارِن',
  'standpoint.comparatist.why': 'عبرت {texts} نصوص دون أن يُطلب منك ذلك.',
  'standpoint.comparatist.effect': 'الأسماء المشتركة والقصص المكرّرة تظهر أولًا.',
  'standpoint.sceptic': 'المتشكّك',
  'standpoint.sceptic.why': 'توقفت حيث تختلف التقاليد.',
  'standpoint.sceptic.effect': 'الهويات المتنازع عليها والروايات المتباينة تبقى بلا تنعيم.',
  'standpoint.originals': 'قارئ الأصل',
  'standpoint.originals.why': 'ظللت تفتح النص الذي تحت الترجمة.',
  'standpoint.originals.effect': 'الأصل يجلس فوق الإنجليزية لا تحتها.',
  'standpoint.newcomer': 'القادم الجديد',
  'standpoint.newcomer.why': 'مشيت بلا خريطة ولم تطلب واحدة.',
  'standpoint.newcomer.effect': 'لا شيء يُقدَّم إليك كأنك تعرفه من قبل.',
  'standpoint.neverAsked': 'لا نسألك أبدًا أيّ تقليد تعتنق. يمكنك تغيير هذا متى شئت.',

  'action.close': 'إغلاق',
  'action.back': 'رجوع',
  'auth.google': 'تسجيل الدخول عبر Google',
  'auth.signedInAs': 'مسجّل الدخول باسم {name}',
  'auth.signOut': 'تسجيل الخروج',
  'auth.rename': 'تغيير الاسم',
  'auth.renameLabel': 'ما تُنادى به هنا',
  'auth.renameSave': 'حفظ',
  'auth.renameCancel': 'إلغاء',
  'auth.renameFailed': 'تعذّر حفظ هذا الاسم.',
  'auth.erase': 'حذف حسابي',
  'auth.eraseWhat': 'هذا يحذف مشاركاتك والمجتمعات التي تديرها والدروس التي وعدت بها. ولا يمكن التراجع عنه.',
  'auth.eraseYes': 'احذف كل شيء',
  'auth.eraseCancel': 'أبقِ حسابي',
  'auth.eraseFailed': 'لم ينجح ذلك — لم يُحذف شيء.',
  'auth.signInDidNotFinish': 'لم يكتمل تسجيل الدخول، ولم يتغيّر شيء. اضغط للإغلاق.',
  'rooms.micDidNotOpen': 'لم يُفتح الميكروفون — اضغط مرة أخرى.',
  'rooms.micStillOn': 'الميكروفون ما زال يعمل — لم نتمكن من إيقافه.',
  'auth.sessionEnded': 'انتهت جلستك، ولذلك لم يحدث ذلك. أعد تحميل الصفحة لتسجيل الدخول من جديد.',
  'rooms.micSilent': 'الميكروفون مفتوح ولا يصدر عنه صوت — تحقق من الجهاز ثم اضغط للمحاولة مرة أخرى.',
  'rooms.notPlaying': 'المتصفح لا يشغّل صوت الغرفة — اضغط هنا لسماعها',
  'rooms.following': 'تتابع الغرفة',
  'rooms.followAgain': 'تابع الغرفة مرة أخرى',
  'rooms.micBlocked': 'متصفحك يمنع الميكروفون في هذا الموقع، ولن تُفتح غرفة بدونه. اسمح به ثم حاول مجددًا.',
  'rooms.yoursHeading': 'غرف فتحتها',
  'rooms.yoursClose': 'إغلاق',
  'rooms.yoursCloseNamed': 'إغلاق {title}',
  'chat.notSent': 'لم تُرسل — كلماتك عادت إلى الصندوق، حاول مرة أخرى.',
  'halls.addressFromName': 'تعذّر اشتقاق عنوان من هذا الاسم — اكتب عنوانًا أدناه بأحرف لاتينية وأرقام.',
  'refusal.suspended': 'تم تعليق هذا الحساب.',
  'refusal.roomFull': 'الغرفة ممتلئة ({capacity}) — حاول بعد لحظة، أو افتح غرفة أخرى.',
  'refusal.stageFull': 'المنصة ممتلئة ({limit}).',
  'refusal.tooFast': 'طلبات كثيرة دفعة واحدة — انتظر لحظة ثم حاول مجددًا.',
  'refusal.addressTaken': 'هذا العنوان مأخوذ.',
  'refusal.tooManyRooms': 'أغلق إحدى غرفك المفتوحة الـ{count} قبل فتح غرفة أخرى.',
  'refusal.tooManyHalls': 'أنت تدير بالفعل {count} قاعات.',
  'refusal.tooManyCoHosts': 'يمكن أن يكون للقاعة {limit} مضيفًا مشاركًا — نحِّ أحدهم أولًا.',
  'refusal.tooManySessions': 'ألغِ إحدى جلساتك الـ{count} المقررة قبل إضافة جلسة أخرى.',
  'refusal.tooManyChannels': 'افصل إحدى قنواتك الـ{count} قبل وصل قناة أخرى.',
  'refusal.hallIsFull': 'تتسع هذه القاعة لـ{limit} عضوًا — على أحدهم المغادرة قبل قبول عضو آخر.',
  'refusal.notAName': 'هذا ليس اسمًا.',
  'refusal.notALink': '{url} ليس رابطًا.',
  'refusal.addressNotUsable': 'هذا العنوان غير صالح — أحرف إنجليزية صغيرة وأرقام وشرطات، وليس كلمة محجوزة.',
  'refusal.notADiscordWebhook': 'هذا ليس عنوان webhook لقناة ديسكورد.',
  'refusal.notAYouTubeChannel': 'هذا ليس عنوان قناة يوتيوب.',
  'refusal.postIsGone': 'لم تعد هذه المشاركة موجودة — ربما أُزيلت.',
  'refusal.voiceUnreachable': 'خدمة الصوت غير متاحة الآن — الغرفة ما زالت هنا، فحاول بعد لحظة.',
  'refusal.silencedButNotMuted': 'الغرفة مضبوطة بحيث لا يتحدث سوى المضيفين، ومن يدخل الآن يدخل مستمعًا — لكن تعذّر الوصول إلى خدمة الصوت لكتم من يتحدثون بالفعل، فحاول بعد لحظة.',
  'refusal.nobodyMuted': 'تعذّر الوصول إلى خدمة الصوت، فلم يُكتم أحد — حاول بعد لحظة.',
  'refusal.stillInTheRoom': 'تعذّر الوصول إلى خدمة الصوت، فما زالوا في الغرفة — حاول بعد لحظة.',
  'refusal.stageNotLive': 'تغيّرت المنصة، لكن تعذّر الوصول إلى خدمة الصوت — سيسري التغيير عند انضمامهم من جديد.',


  'hint.text': 'انقر على أيّ اسم لفتحه — ثم اتبع الروابط في كلّ آية للتنقّل بين الكتب المقدّسة.',
  'hint.dark': 'الظلام هو ما لم تستكشفه بعد',
  'hint.zoomIn': 'اقترب لقراءة الأسماء',
  'hint.tap': 'انقر على اسم لفتح آياته',
  'hint.flying': 'esc للخروج · اسحب لمواصلة التحليق',
  'hint.dismiss': 'إغلاق',

  'type.person': 'شخص',
  'type.place': 'مكان',
  'type.people': 'قوم',
  'type.divine': 'الإلهي',
  'type.unknown': 'غير مُصنَّف',
  'type.object': 'شيء',
  'type.observance': 'مناسبة',
  'type.institution': 'مؤسسة',
  'type.scripture': 'كتاب',
  'type.angelic': 'ملاك أو روح',
  'type.deity': 'إله',
  'type.time': 'زمن',
  'type.language': 'لغة',
  'type.practice': 'عبادة',
  'type.ruling': 'حكم',
  'type.event': 'حدث',
  'type.realm': 'عالم',
  'type.attribute': 'اسم أو صفة',
  'type.religion': 'دين',
  'type.tribe': 'قبيلة',
  'type.celestial': 'كوكبة',
  'type.concept': 'مفهوم',
  'type.parable': 'مثل',
  'type.prayer': 'دعاء',
  'type.creature': 'مخلوق',

  'panel.about': 'عن {name}',
  'panel.openDiscussion': 'فتح النقاش',
  'panel.namedIn': 'مذكور في',
  'panel.reachAll': '{name} مذكور فيها جميعًا.',
  'panel.reachSome': '{name} مذكور في {named} من {total} كتب.',
  'panel.reachOnly': '{name} مذكور في {text} فقط.',
  'panel.onlyThis': 'إظهار {text} فقط',
  'panel.trails': 'مسارات من هنا',
  'panel.inward': 'إلى الداخل',
  'panel.outward': 'إلى الخارج',
  'panel.linkedBy': 'مرتبط عبر {ref}',
  'panel.sharesName': 'شخصية أخرى تحمل هذا الاسم:',
  'panel.identity': 'أسماء قيل إنها لذات واحدة:',
  'panel.argued': 'موضع خلاف، في هذه الآيات:',
  'panel.identityHeld': 'القائلون',
  'panel.identityDisputed': 'المخالفون',
  'unit.passage': 'مقطع',
  'unit.passages': 'مقاطع',
  'panel.contested': 'هناك خلاف حول ما إذا كانت هذه شخصية واحدة عبر الكتب المقدّسة.',
  'panel.shared': 'مقاطع مشتركة',
  'panel.reports': 'ورد أيضًا عن {name}',
  'panel.reportsArabicOnly': 'معظمها بالعربية فقط. تأذن HadeethEnc بترجمتها الإنجليزية وتُعرض حيثما طابقت حديثًا هنا — لكنها مختارات موسوعة سنية، فتصل إلى المجموعات السنية دون أيٍّ من المجموعات الإمامية أو الزيدية أو الإباضية. وفي غيرها توجد ترجمات إنجليزية محفوظة الحقوق ولم تُمنح هذه المكتبة إذنًا بها.',
  'panel.reportsEnglishFrom': 'ترجمة إنجليزية من HadeethEnc',
  'panel.reportsCut': 'هذا الحديث مبتور — المصدر المأخوذ عنه ينقطع هنا في وسط الكلمة.',
  'panel.reportsCutTitle': 'ملف المصدر يحدّ كل حديث بمئتي حرف. بقية النص لم تُسلَّم أصلًا، وهي ليست ناقصة من المجموعة نفسها.',
  'panel.reportsNumbering': 'حين تكون الإحالة هنا اسمًا ورقمًا فقط، فهي ترقيم ناشر النسخة الرقمية لا ترقيم طبعة مطبوعة، ولا تطابق الإحالات المنقولة من مصادر أخرى.',
  'panel.reportsChain': 'سند الحديث',
  'panel.reportsChainWho': 'السطر تحت الاسم هو من يقول المصدر إنه ذاك الرجل ومتى توفي — والإسناد يذكر الاسم القصير الذي عرفه به سامعوه. أما الاسم فوقه فهو ما يطبعه الكتاب نفسه.',
  'panel.reportsCites': 'يقتبس',
  'panel.reportsAlso': 'يذكر أيضا',
  'panel.reportsAgreed': 'وأيضا في',
  'panel.reportsAgreedTitle': 'متفق عليه — أخرجه الصحيحان',
  'panel.reportsPlaceTitle': 'موضع الحديث من الكتاب — كتابه وبابه عند المصنف، محددًا من طبعة مسماة',
  'panel.reportsPlaceInferredTitle': 'الكتاب مستنتج مما قبله وما بعده من الأحاديث الواقعة فيه، والباب غير معلوم',
  'panel.reportsQuotedFrom': 'نقلا عن',
  'panel.reportsQuotedFromTitle': 'المصدر الأقدم الذي أخذ عنه الخبر. مستدرك الوسائل مستدرك: جمع فيه النوري ما فات صاحب الوسائل، فأخباره أقدم من الكتاب الحامل لها.',
  'panel.reportsGraded': 'حكم عليه {who}',
  'panel.reportsGradedTitle': 'حكم المصنف نفسه على خبره بلفظه، لا حكم هذه المكتبة ولا حكم محقق متأخر. الحكم رأي عالم في خبر، فيعرض باسمه لأنه بغير اسمه لا معنى له.',
  'panel.gradeHasanSahih': 'حسن صحيح',
  'panel.gradeHasanGharib': 'حسن غريب — لم يرو إلا من وجه واحد',
  'panel.gradeSahih': 'صحيح',
  'panel.gradeHasan': 'حسن',
  'panel.gradeGharib': 'غريب — لم يرو إلا من وجه واحد',
  'panel.gradeDaif': 'ضعيف',
  'panel.gradeMunkar': 'منكر',
  'panel.gradeMursal': 'مرسل — انقطع الإسناد دون النبي',
  'panel.gradeMudtarib': 'مضطرب — اختلفت طرقه',
  'panel.gradeMukhtasar': 'مختصر — من حديث أطول',
  'panel.gradeMansukh': 'منسوخ — نسخه حكم متأخر',
  'panel.gradeMalul': 'معلول — فيه علة خفية',
  'panel.branchSunni': 'سني',
  'panel.branchTwelver': 'شيعي إمامي',
  'panel.branchZaydi': 'شيعي زيدي',
  'panel.branchIbadi': 'إباضي',
  'panel.branchTitle': 'أي فرع من الإسلام يحمل المجموعة التي ورد فيها هذا الخبر. تحمل هذه المكتبة رفوفًا من أربعة فروع، وصفحة الشخصية تعرضها معًا.',
  'panel.layerMarfu': 'مرفوع — عن النبي',
  'panel.layerQudsi': 'قدسي — كلام الله بلفظ النبي',
  'panel.layerMawquf': 'موقوف — ينتهي إلى صحابي',
  'panel.layerMaqtu': 'مقطوع — ينتهي إلى تابعي',
  'panel.reportsSplit': '{named} تذكره · {narrated} رواها',
  'panel.reportsNarrated': 'رواه {name}',
  'panel.sharedLead': 'الحادثة نفسها، مَرويّة في التوراة والقرآن — للقراءة جنبًا إلى جنب.',
  'panel.differLead': 'حيث يختلف سرد هذه الشخصية بين التوراة والقرآن — اقرأ الاثنين، كسؤال، لا كحُكم أبدًا.',
  'panel.filterPlaceholder': 'تصفية — نصّ أو كتاب أو 1:1',
  'panel.filterLabel': 'تصفية مقاطع {name}',
  'panel.chipsLabel': 'إظهار أو إخفاء كتاب',
  'panel.noMatch': 'لا توجد مقاطع مطابقة.',
  'panel.showing': 'عرض {shown} من {total}',
  'panel.sortLabel': 'ترتيب',
  'panel.sortInOrder': 'حسب الترتيب',
  'panel.sortLeadFirst': '{name} أولًا',
  'panel.noPassages': 'لا آيات مرتبطة هنا — تشير الكتب المقدّسة إلى هذه الشخصية بالوصف، أو باسمٍ شائع أكثر من أن يُوسم آيةً آية، فلا توجد قائمة مقاطع تُفتح.',
  'panel.noPassagesReportsOnly': 'لا يذكر هذه الشخصية الكتاب المقدّس ولا القرآن. وهي هنا لأنّ الحديث يذكرها — وما رُوي عنها مذكور أعلاه.',

  'verses.loading': 'جارٍ فتح المقاطع…',
  'verses.loadingCount': 'جارٍ فتح {count} مقاطع…',
  'verses.heading': 'المقاطع',
  'verses.orderEach': 'مرتّبة واحدة من كلٍّ بالتناوب.',
  'verses.orderFrom': 'مرتّبة ابتداءً من {text}، ثم واحدة من كلٍّ بالتناوب.',
  'verses.notLoaded': 'لم يُحمَّل هذا المقطع.',
  'verses.otherTranslations': '{n} أخرى',
  'verses.nonCommercialTitle': 'رخصة هذه الترجمة تمنع بيعها.',
  'verses.nonCommercial': 'غير تجاري',
  'verses.hebrewNumbering': 'العبرية',
  'verses.hebrewNumberingWhy': 'المراجع هنا تتبع ترقيم الملك جيمس. الكتاب المقدس العبري يرقّم هذا الإصحاح بشكل مختلف.',
  'verses.noOriginal': 'لا يتضمن {edition} نصًّا عند هذا الموضع.',
  'verses.readOn': 'اقرأ على {source}',
  'verses.discuss': 'ناقش هذه الآية',
  'verses.incomplete': 'تعذّر تحميل بعض المقاطع — الروابط أعلاه تفتحها في مكان آخر.',

  'translate.hide': 'إخفاء الترجمة الآلية',
  'translate.to': 'ترجمة آلية إلى {lang}',
  'translate.inProgress': 'جارٍ الترجمة…',
  'translate.caveat': 'آلية — قراءة تقريبية بالآلة، وليست ترجمة معتمدة.',
  'translate.unsupported': 'لا تتوفّر ترجمة {lang} على الجهاز في هذا المتصفّح.',
  'translate.error': 'تعذّرت ترجمة هذه الآية.',

  'discussion.edit': 'تحرير',
  'discussion.editing': 'صحّح كلماتك',
  'discussion.save': 'حفظ',
  'discussion.cancelEdit': 'إلغاء',
  'discussion.edited': '(مُحرَّر)',
  'discussion.report': 'أبلغ',
  'discussion.reported': 'تم الإبلاغ',
  'discussion.remove': 'إزالة',
  'discussion.postError': 'تعذّر النشر — يُرجى المحاولة مرّة أخرى.',
  'discussion.label': 'نقاش',
  'discussion.add': 'أضِف إلى النقاش',
  'discussion.placeholder': 'أضِف إلى النقاش…',
  'discussion.post': 'نشر',
  'discussion.toTakePart': ' للمشاركة.',
  'discussion.loading': 'جارٍ تحميل النقاش…',
  'discussion.earlier': 'المشاركات السابقة',
  'discussion.earlierFailed': 'تعذّر تحميل المشاركات السابقة.',
  'discussion.empty': 'لم يكتب أحد هنا بعد.',
  'discussionDrawer.label': 'نقاش — {title}',
  'discussionDrawer.heading': '{title} — نقاش',

  'voice.label': 'غرفة صوتية',
  'voice.heading': 'صوت',
  'voice.leave': 'مغادرة الصوت',
  'voice.toJoin': ' للانضمام.',

  'reports.heading': 'منشورات مُبلَّغ عنها',
  'reports.failed': 'لم يُنفَّذ ذلك — لم يتغيّر شيء. حاول مرة أخرى.',
  'reports.keep': 'اتركه',
  'reports.remove': 'أزِله',
  'reports.times': 'بُلِّغ عنه {count} مرّات',
  'reports.departed': 'قارئ غادر',
  'reports.more': 'و{count} في الانتظار',
  'reports.suspend': 'تعليق حساب {name}',
  'reports.reinstate': 'إعادته',
  'reports.history': 'ما فعله المشرفون',
  'reports.earlier': 'أقدم',
  'reports.wasSuspended': 'أوقفه',
  'reports.wasSomething': 'جرى التعامل معه بواسطة',
  'reports.wasReinstated': 'أعاده',
  'rooms.empty': 'لا أحد يتحدّث هنا بعد.',
  'rooms.openOne': 'افتح غرفة',
  'rooms.whoMayCome': 'من يمكنه الدخول',
  'rooms.accessPublic': 'الجميع',
  'rooms.accessPassphrase': 'بكلمة سرّ',
  'rooms.accessInvite': 'بالدعوة فقط',
  'rooms.setPassphrase': 'عيّن كلمة سرّ',
  'rooms.invitePlaceholder': 'الصق رمز دعوة',
  'rooms.useInvite': 'استخدمه',
  'rooms.shareInvite': 'أرسل هذا الرمز للناس: ',
  'rooms.underHall': 'افتحها باسم',
  'rooms.commons': 'الجميع',
  'halls.couldNotReadYours': 'تعذّرت قراءة مجتمعاتك الآن، لذا لن يُفتح هذا تحت أي منها.',
  'rooms.titlePlaceholder': 'عن ماذا هذه الغرفة؟',
  'rooms.opening': 'يفتح…',
  'rooms.roomLabel': 'الانضمام إلى «{title}»',
  'rooms.inRoom': '{count} هنا',
  'rooms.nobodyYet': 'فارغة',
  'rooms.full': 'ممتلئة ({count})',
  'rooms.barred': 'هذه الغرفة ليست مفتوحة لك.',
  'rooms.notHost': 'المضيف وحده يستطيع ذلك.',
  'rooms.close': 'إغلاق الغرفة',
  'rooms.barredHeading': 'ممنوعون من هذه الغرفة',
  'rooms.letBackIn': 'السماح لـ{name} بالعودة',
  'rooms.departedReader': 'قارئ غادر',
  'rooms.lockDoor': 'إقفال الباب',
  'rooms.openDoor': 'الفتح للجميع',
  'rooms.newCode': 'رمز جديد',
  'rooms.silence': 'أسكِت الغرفة',
  'rooms.hostBadge': 'مضيف',
  'rooms.listening': 'يستمع',
  'rooms.unheard': 'لا يصلك',
  'rooms.muteSelf': 'كتم صوتي',
  'rooms.unmuteSelf': 'إلغاء كتم صوتي',
  'rooms.participants': 'في هذه الغرفة',
  'rooms.stageSeats': '{taken} من {limit} على المنصة',
  'rooms.showEveryone': 'إظهار البقية ({count})',
  'rooms.moreAsking': '{count} في الانتظار',
  'rooms.forceMute': 'كتم {name}',
  'rooms.hushForMe': 'اكتم {name} عندي فقط',
  'rooms.hearAgain': 'اسمع {name} مرّة أخرى',
  'rooms.toStage': 'اسمح لـ{name} بالتحدّث',
  'rooms.offStage': 'أوقف تحدّث {name}',
  'rooms.kick': 'إزالة {name}',
  'rooms.ban': 'حظر {name}',
  'rooms.makeHost': 'اجعل {name} مضيفًا',
  'rooms.deskOpen': 'افتح غرفة، أو استخدم دعوة',
  'rooms.talkHere': 'تحدّث عن هذا',
  'rooms.locked': 'يحتاج كلمة سرّ',
  'rooms.passphrasePlaceholder': 'كلمة السرّ',
  'rooms.enter': 'دخول',
  'rooms.back': 'رجوع',
  'rooms.wrongPassphrase': 'كلمة السرّ هذه لا تفتح الغرفة.',
  'rooms.wentWrong': 'لم ينجح ذلك الآن. لم يُفقد شيء — حاول مرّة أخرى بعد قليل.',
  'rooms.reconnecting': 'انقطع الاتصال — نحاول العودة إلى الغرفة…',
  'rooms.handUp': 'أطلب الكلام',
  'rooms.handDown': 'لا داعي',
  'rooms.asking': '{count} يطلبون الكلام',
  'rooms.notNow': 'ليس الآن، {name}',
  'rooms.speakNext': 'اسمح لـ{name} بالتحدّث التالي',
  'rooms.coHostAsked': 'دعوتم مجتمعًا آخر للاستضافة المشتركة. ',
  'rooms.coHosting': 'مستضاف بالاشتراك مع مجتمع آخر. ',
  'rooms.coHostEnd': 'إنهاء ذلك',
  'rooms.coHostAsk': 'ادعُهم للاستضافة المشتركة',
  'rooms.coHostPlaceholder': 'عنوان مجتمعهم',
  'rooms.coHostNoSuchHall': 'لا يوجد مجتمع بهذا العنوان.',
  'rooms.removedYou': 'أخرجك أحد المضيفين من تلك الغرفة.',
  'rooms.hostClosed': 'أغلق المضيف تلك الغرفة.',
  'rooms.joinedElsewhere': 'انضممت إلى تلك الغرفة من مكان آخر، فأُنهي هذا الاتصال.',
  'rooms.connectionLost': 'فُقد الاتصال بتلك الغرفة.',

  'chat.label': 'دردشة الغرفة',
  'chat.heading': 'دردشة',
  'chat.empty': 'لم يُقل شيء منذ وصولك.',
  'chat.placeholder': 'اكتب شيئًا…',
  'chat.send': 'إرسال',

  'sessions.label': 'الجلسات المجدولة',
  'sessions.heading': 'قادم',
  'sessions.empty': 'لم يُجدول شيء هنا بعد.',
  'sessions.weekly': 'كل أسبوع',
  'sessions.joinNow': 'انضم الآن',
  'shared.timeUnknown': 'وقت غير معروف',
  'sessions.underHall': 'أقمها باسم',
  'sessions.cancel': 'إلغاء',
  'sessions.yours': 'جلساتك في أماكن أخرى',
  'sessions.goThere': 'الانتقال إلى {name}',
  'sessions.cancelThis': 'إلغاء {name}',
  'sessions.whenLabel': 'متى',
  'sessions.titlePlaceholder': 'ماذا ستدرسون؟',
  'sessions.everyWeek': 'كل أسبوع',
  'sessions.startsIn': 'بعد {count} دقيقة',
  'sessions.lasts': 'لمدة {count} دقيقة',
  'sessions.startingNow': 'يبدأ الآن',
  'sessions.howLong': 'مدة اللقاء',
  'sessions.minutes': '{count} دقيقة',
  'sessions.schedule': 'جدولة',
  'sessions.scheduling': 'تتم الجدولة…',
  'sessions.toSchedule': ' لجدولة جلسة.',
  'sessions.badTime': 'هذا ليس وقتًا صالحًا.',
  'sessions.failed': 'لم ينجح ذلك — حاول مجددًا.',
  'sessions.newPassphrase': 'كلمة سر جديدة',
  'sessions.changeIt': 'غيّرها',
  'sessions.rekeyThis': 'تغيير كلمة سر {name}',
  'sessions.whoMayCome': 'من يمكنه حضور الجلسة',
  'sessions.setPassphrase': 'كلمة سر للجلسة',
  'sessions.calledOff': 'أُلغي',
  'sessions.hallGone': 'المجتمع الذي أقيمت تحته حُذف',
  'sessions.notPermitted': 'لم يعد بإمكانك عقد جلسات تحت ذلك المجتمع',

  'channels.heading': 'الإعلان في ديسكورد',
  'channels.connect': 'ربط',
  'channels.connecting': 'يتم الربط…',
  'channels.urlLabel': 'رابط Webhook في ديسكورد',
  'channels.urlHelp': 'في ديسكورد: إعدادات القناة ← التكاملات ← Webhooks ← نسخ الرابط.',
  'channels.thisPassage': 'عند فتح غرفة على هذا المقطع',
  'channels.everyPassage': 'عند فتح غرفة في أي مكان',
  'channels.followsThis': 'يعلن عن الغرف على هذا المقطع',
  'channels.followsAll': 'يعلن عن الغرف في كل مكان',
  'channels.retired': 'توقّف — أعد ربط القناة',
  'channels.retiredDeleted': 'توقّف — لم يعد هذا الويبهوك موجودًا في ديسكورد. أنشئ واحدًا جديدًا وصِله.',
  'channels.retiredRefused': 'توقّف — رفضه ديسكورد. تحقّق من أذونات القناة ثم أعد التوصيل.',
  'channels.followsSome': 'يعلن عن {count} مقاطع',
  'channels.disconnect': 'فصل',
  'channels.reconnect': 'إعادة الربط',
  'channels.none': 'لا توجد قناة مرتبطة.',
  'channels.failed': 'لم ينجح ذلك — حاول مجددًا.',

  'hall.label': 'قاعة',
  'hall.loading': 'يفتح القاعة…',
  'hall.missing': 'لا توجد قاعة على هذا العنوان.',
  'hall.unreadable': 'تعذّرت قراءة هذا المجتمع الآن — ما زال موجودًا، حاول بعد لحظة.',
  'hall.standingUnknown': 'تعذّر تحديد ما إذا كنت في هذا المجتمع الآن — حاول بعد لحظة.',
  'hall.back': 'العودة إلى الخريطة',
  'hall.verifiedMark': '✓ موثّقة',
  'hall.verifiedAs': 'أُثبتت السيطرة على {handle}',
  'hall.join': 'الانضمام إلى القاعة',
  'hall.leave': 'مغادرة',
  'hall.settings': 'الصفحة الأمامية لقاعتك',
  'hall.nameLabel': 'الاسم',
  'hall.descriptionLabel': 'ما الغرض منها',
  'hall.linksLabel': 'روابط خارجة',
  'hall.linkKind': 'نوع الرابط',
  'hall.linkUrl': 'العنوان',
  'hall.linkLabel': 'بماذا تسمّيه',
  'hall.addLink': 'أضف رابطًا',
  'hall.dropLink': 'إزالة',
  'hall.doorLabel': 'من يمكنه الانضمام',
  'hall.doorOpen': 'يمكن لأي شخص الانضمام',
  'hall.doorApproval': 'توافق على كل شخص',
  'hall.listingLabel': 'في الدليل',
  'hall.listingPublic': 'مُدرجة',
  'hall.listingUnlisted': 'بالرابط فقط',
  'hall.save': 'حفظ',
  'hall.saving': 'يحفظ…',
  'hall.team': 'من يساعدك في إدارتها',
  'shared.couldNotRead': 'تعذّرت قراءة هذا الآن — ليس فارغًا، حاول بعد لحظة.',
  'hall.bar': 'حظر {name}',
  'hall.barred': 'محظورون من هذه الجماعة',
  'hall.letBackIn': 'إعادة {name}',
  'hall.coHost': 'مضيف مشارك',
  'hall.makeCoHost': 'عيّنه مضيفًا مشاركًا',
  'hall.standDown': 'أنهِ التعيين',
  'hall.departed': 'قارئ غادر',
  'hall.log': 'ما فعله مشرفوك',
  'hall.logEarlier': 'أقدم',
  'hall.showMore': 'عرض المزيد',
  'hall.invited': 'دُعيتم للاستضافة المشتركة',
  'hall.acceptCoHost': 'استضِف معهم',
  'hall.declineCoHost': 'لا، شكرًا',
  'log.mute': 'كتم {name}',
  'log.onStage': 'سمح لـ{name} بالتحدّث',
  'log.offStage': 'أنزل {name} عن المنصّة',
  'log.remove': 'أخرج {name}',
  'log.ban': 'حظر {name}',
  'log.unban': 'رفع الحظر عن {name}',
  'log.addHost': 'جعل {name} مضيفًا',
  'log.silence': 'كتم الغرفة',
  'log.closeRoom': 'أغلق الغرفة',
  'log.doorLocked': 'أقفل الباب',
  'log.doorOpened': 'فتح الباب للجميع',
  'log.unnamed': 'اتخذ إجراءً بشأن {name} — هذه الصفحة أقدم من السجل',
  'log.moderatorAdded': 'عيّن {name} مضيفًا مشاركًا',
  'log.moderatorRemoved': 'أنهى دور {name} كمضيف مشارك',
  'log.hallBanned': 'حظر {name} من الجماعة',
  'log.hallUnbanned': 'أعاد {name} إلى الجماعة',
  'log.coHostAsked': 'دعا مجتمعًا آخر للاستضافة المشتركة',
  'log.coHostAccepted': 'وافق على استضافة غرفة مجتمع آخر',
  'log.coHostEnded': 'أنهى الاستضافة المشتركة',
  'hall.waiting': 'في انتظار الانضمام',
  'hall.waitingToBeLetIn': 'طلبت الانضمام. سيأذن لك أحد القادة.',
  'hall.withdraw': 'سحب الطلب',
  'hall.approve': 'اسمح لهم بالدخول',
  'hall.asked': 'طلبوا {when}',
  'hall.talkingNow': 'يتحدثون الآن',
  'hall.comingUp': 'قريباً',
  'hall.roomHere': '{count} هنا',
  'hall.roomQuiet': 'فُتحت للتو',
  'hall.cannotJoin': 'لا يمكنك الانضمام إلى هذه القاعة.',
  'hall.ownerOnly': 'المالك وحده يستطيع ذلك.',
  'hall.ownerTools': 'لك أن تديرها',
  'hall.delete': 'احذف هذه القاعة',
  'hall.deleteConfirm': 'اكتب {slug} للتأكيد',
  'hall.deleteFinal': 'احذفها نهائيًا',
  'hall.deleteCancel': 'أبقِها',
  'hall.export': 'تنزيل كل شيء',
  'hall.verify': 'أثبت أنها لك',
  'hall.handlePlaceholder': 'قناتك على يوتيوب',
  'hall.challengeLead': 'ضع هذا في وصف {handle} ثم تحقّق.',
  'hall.checkNow': 'تحقّق الآن',
  'hall.toTakePart': ' للمشاركة.',
  'hall.failed': 'لم ينجح ذلك — حاول مجددًا.',
  'halls.label': 'القاعات',
  'halls.heading': 'القاعات',
  'halls.yours': 'قاعاتك',
  'halls.public': 'مفتوحة للجميع',
  'halls.none': 'لا توجد قاعات بعد.',
  'halls.noneYours': 'لست في قاعة بعد.',
  'halls.startOne': 'ابدأ قاعة',
  'halls.listInDirectory': 'اعرضها حيث يمكن للناس أن يجدوها',
  'hall.memberCount': '{count} عضوًا',
  'hall.memberOfLimit': '{count} من {limit} عضوًا',
  'halls.opening': 'يفتح…',
  'halls.namePlaceholder': 'ما اسم مجتمعك؟',
  'halls.addressPlaceholder': 'العنوان',
  'halls.toStart': ' لبدء قاعة.',
  'halls.failed': 'لم ينجح ذلك — حاول مجددًا.',
  'halls.browse': 'القاعات',

  'reader.label': 'قراءة {unit}',
  'reader.labelFallback': 'قراءة المقطع',
  'reader.heading': 'مقطع',
  'reader.alsoTold': 'مَروي أيضًا في {ref}',
  'reader.alsoCounted': 'معدود بشكل مختلف في {ref}',

  'story.sharedTitle': '{figure} — حادثة واحدة، كتابان',
  'story.sharedCaption': 'اقتُرِنا لأن كليهما يستخدمان: {shared}. اقتراح بقراءة الاثنين، لا ادّعاء بأنهما يقولان الشيء نفسه.',
  'story.toldTitle': '{group} — مَروي مرّتين',
  'story.toldCaption': '{count} آيات مشتركة — روايتان داخل كتاب واحد، معروضتان جنبًا إلى جنب، دون حُكم.',

  'told.lead': 'الإصحاح نفسه، مَروي مرّتين داخل كتاب واحد — معروض جنبًا إلى جنب، دون حُكم.',
  'told.shared': '{count} مشتركة',

  'differ.lead': 'مواضع تروي فيها التوراة والقرآن حادثة واحدة على نحو مختلف — معروضة كسؤال مع القراءتين، لا كحُكم.',
  'numbers.lead': 'مواضع يعطي فيها كتاب واحد رقمين مختلفين للشيء نفسه — مع التنبيه إلى أن الكلمة نفسها قد تَعُدّ أشياء مختلفة.',
  'differences.readBoth': 'اقرأ الروايتين',

  'disputed.badgeSameName': 'الاسم نفسه، غالبًا مختلفان',
  'disputed.badgeDisputed': 'هوية مُختلَف عليها',
  'disputed.heading': 'هويّات مُختلَف عليها',
  'disputed.lead': 'شخصيات تُسمّيها التقاليد — أو لا — بالطريقة نفسها. أهي شخص واحد عبر الكتب المقدّسة، أم اثنان يتشاركان اسمًا؟ اقرأ الحالة، ثم أدلِ برأيك. معروضة كسؤال مفتوح، لا كحُكم أبدًا.',
  'disputed.weighIn': 'أدلِ برأيك',
  'disputed.seeOnMap': 'اعرض {name} على الخريطة',
  'contested.passagesHeading': 'مقاطع يدور حولها الجدل',
  'contested.passagesLead': 'مقيسة لا مختارة: هذه مقاطع يتجادل حولها أكثر من تقليد واحد. افتح المقطع واقرأه.',
  'contested.tradition.christian': 'مسيحي',
  'contested.tradition.muslim': 'مسلم',
  'contested.tradition.jewish': 'يهودي',
  'contested.open': 'مواضع الخلاف',
  'contested.alongside': 'يُناقَش مع',

  'sources.lead': 'كلّ ترجمة مذكورة باسمها، وما المحجوب ولماذا.',
  'sources.labelEnglish': 'الإنجليزية',
  'sources.labelSecond': 'إنجليزية ثانية',
  'sources.labelThird': 'إنجليزية ثالثة',
  'sources.labelAlso': 'وأيضاً في',
  'sources.renderingGate': 'يُعرض كل من هذه النصوص فقط حيث يمكن مطابقته آيةً بآية، ويُحذف بدلاً من عرضه مقابل الآية الخطأ. والسبب في الغالب هو تقسيم الإصحاحات: ترجمة تقسّم الآيات على نحو مختلف لا يمكن محاذاتها. وحيث ينقص نصٌّ لسبب آخر — قانون لم يضم تلك الأسفار قط، أو نسخة فيها فجوات — فملاحظته الخاصة تبيّن السبب.',
  'sources.labelOriginal': 'الأصل',
  'sources.noEnglish': 'لا توجد ترجمة إنجليزية منشورة — يُقرأ بالعربية، أو كقراءة آلية مُعلَّمة',
  'sources.heldBack': 'محجوب',
  'sources.heldBackAffects': 'يخص',
  'sources.absent': 'مذاهب غير ممثلة هنا',
  'sources.absentLead': 'لا يمثَّل أيٌّ من هذه التقاليد في هذه المكتبة بعد. وفي كل حالة يعود السبب إلى الترخيص أو الرقمنة من جانبنا، وليس حكماً على النصوص نفسها.',
  'sources.absentSplit': 'من بين {total}، {awaited} غائبة تماماً — لم يأذن أحد. أمّا الـ{partly} الأخرى فبعض نصّها موجود هنا بالفعل ولا يمكن عرضه بعد كما يحتاجه قارئ ذلك الفرع.',
  'sources.absentHolds': 'ما يبحث عنه قارئها',
  'sources.unshelved': 'محفوظة ولم تُصنَّف بعد',
  'sources.unshelvedLead': 'هذه النصوص متوفرة لدينا ويجوز نشرها. {count} مقطعًا تنتظر قرارًا بشأن موضعها — التأخير من جانبنا.',
  'sources.unshelvedPassages': 'مقطعًا محفوظًا',
  'sources.blockedPermission': 'بانتظار الإذن',
  'sources.blockedOriginal': 'لا يوجد أصل قابل للنشر',
  'sources.blockedAddressing': 'غير مقسّم إلى آيات',
  'sources.blockedTranslation': 'لا توجد ترجمة منشورة',
  'sources.blockedTranscription': 'متاح بحرية، لكن لم يُنسخ بدقة بعد',
  'sources.expiries': 'ستتاح في تاريخ محدد',
  'sources.expiriesLead': 'ليست مفقودة إلى الأبد — هي محمية بحقوق النشر حتى انتهاء المدة، والتاريخ معروف.',
  'sources.freeFrom': 'تتاح في {date}',
  'sources.expiryMonths': 'بعد نحو {n} شهرًا',
  'sources.expiryYears': 'بعد نحو {n} سنوات',
  'sources.heldAsScripture': 'يُعدّ كتابًا مقدّسًا عند',
  'sources.notHeldAsScripture': 'وليس عند',
  'sources.alsoHeldBy': 'ويُعدّ كتابًا مقدّسًا أيضًا عند',
  'sources.notInCanonOf': 'ليست ضمن قانون',
  'sources.heldBackLead': 'غير مُضمَّن — مسألة ترخيص، لا مسألة تحرير.',
  'sources.awaiting': 'بانتظار {name}',

  'search.label': 'ابحث عن اسم في الخريطة',
  'search.placeholder': 'ابحث عن اسم — إبراهيم، موسى…',
  'search.nothing': 'لا يوجد اسم هنا يطابق «{term}».',
  'search.openPassage': 'افتح {ref} ←',

  'offline.saving': 'جارٍ الحفظ للاستخدام دون اتصال…',
  'offline.savingCount': 'جارٍ الحفظ للاستخدام دون اتصال… {done} من {total}',
  'offline.save': 'حفظ للاستخدام دون اتصال',
  'offline.saved': 'حُفِظ {saved} من {total} ملفات للقراءة دون اتصال.',
  'offline.error': 'تعذّر الحفظ للاستخدام دون اتصال.',

  'legend.open': 'المفتاح — ما الذي أنظر إليه؟',
  'legend.scriptures': 'الكتب المقدّسة',
  'legend.shapes': 'الأشكال',
  'legend.people': 'قوم أو أمّة',
  'legend.shapeBeings': 'شخص، أو من يُعبَد',
  'legend.shapeThings': 'مكان أو شيء أو كتاب',
  'legend.shapeBodies': 'قوم أو موسم أو مؤسسة',
  'legend.noScripture': 'رمادي — لا يسمّيه أيّ كتاب؛ موصوف دون تسمية، أو مذكور في الحديث وحده',
  'legend.marks': 'العلامات',
  'legend.size': 'أكبر — مذكور في آيات أكثر؛ أمّا الباهتة فمرسومة بمقياس أصغر خاصّ بها',
  'legend.gold': 'حلقة ذهبية — هناك خلاف حول ما إذا كانت هذه شخصية واحدة عبر الكتب المقدّسة؛ افتحها لقراءة الحالة وإبداء الرأي',
  'legend.faint': 'باهت — اكتُشف من قراءة النصوص، لم يُراجَع يدويًا',
};

/** English is complete; every other language is a partial overlay on top of it. */
export const DICT: { en: Strings } & Record<LangCode, Partial<Strings>> = { en, he, ar };

/** The phrase for `key` in `lang`, falling back to English when the language omits it, then filled. */
export function translate(lang: LangCode, key: keyof Strings, params?: Record<string, string | number>): string {
  const template = DICT[lang][key] ?? DICT.en[key];
  return fill(template, params);
}
